import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import InputMask from 'react-input-mask';
import {
  FaCar, FaFileInvoice, FaMoneyBillWave, FaCalculator, FaPlus, FaTrash,
  FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaSync, FaTimes, FaUser,
  FaCog, FaPrint, FaEdit, FaCheck, FaSearch, FaMapMarkerAlt, FaExclamationTriangle,
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { decodeToken, isExpired } from 'react-jwt';
import { consultarCnpj, completarComDadosCnpj, avisoCompletarCnpj } from '../others/consultaCnpj';
import ImportarPrintCliente, { ESTILO_CAMPO_DO_PRINT, cepVeioDoPrint } from '../components/ImportarPrintCliente';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hojeISO = () => {
  const h = new Date();
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}-${String(h.getDate()).padStart(2, '0')}`;
};

const formatarMoeda = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '0,00';
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

const formatarDataBR = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

// ── NOVO: data + horário do registro (ex: "20/07/2026 14:32"), usado nas
// tabelas de Procurações e Pix pra identificar quando cada lançamento foi
// feito de verdade — não só a data do dia selecionado. ──
const formatarDataHoraBR = (isoDateTime) => {
  if (!isoDateTime) return '';
  const d = new Date(isoDateTime);
  if (isNaN(d.getTime())) return '';
  const dataStr = d.toLocaleDateString('pt-BR');
  const horaStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${dataStr} ${horaStr}`;
};

// ── CORREÇÃO: quadro do CRLV-e agora é uma grade fixa estilo planilha ──
const NUM_COLUNAS_CRLV = 10;
const NUM_LINHAS_CRLV = 20;

// Valor numérico de uma célula da grade para a soma da seleção (como no Google Planilhas): aceita
// "150", "150,50", "1.500,00", "R$ 20" e "12.5". Placa ou texto devolve null (só entra na contagem).
const numeroDaCelula = (texto) => {
  let t = (texto || '').replace(/R\$/gi, '').replace(/\s/g, '');
  if (!/^-?[\d.,]+$/.test(t) || !/\d/.test(t)) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '');
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

const formatarNumeroBR = (n) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(n);

// Presença na grade: cada estação ganha uma cor fixa (derivada da sessão) e aparece pelo primeiro nome
const CORES_PRESENCA_CRLV = ['#e67e22', '#8e44ad', '#2980b9', '#c0392b', '#16a085', '#d35400', '#2c3e50', '#27ae60'];
const corDaSessaoCrlv = (sessaoId) => {
  let h = 0;
  for (const ch of String(sessaoId || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CORES_PRESENCA_CRLV[h % CORES_PRESENCA_CRLV.length];
};
const primeiroNomeCrlv = (nome) => (nome || '').trim().split(/\s+/)[0] || 'Usuário';

// ── NOVO: atalho de saque (Pix/ISSQN) — marcador usado na descrição do
// saque pra identificar, ao recarregar a página, se o saque do dia já foi
// feito por aqui (evita duplicar o saque se o usuário atualizar a tela) ──
const MARCADOR_SAQUE_PIX = '[Atalho Pix]';
const MARCADOR_SAQUE_ISSQN = '[Atalho ISSQN]';

const ABAS = [
  { id: 'crlv', label: 'CRLV-e', icon: FaCar },
  { id: 'procuracoes', label: 'Procurações', icon: FaFileInvoice },
  { id: 'pix', label: 'Pix', icon: FaMoneyBillWave },
  { id: 'issqn', label: 'ISSQN', icon: FaCalculator },
  { id: 'vistoria', label: 'Vistoria Fora da Sede', icon: FaMapMarkerAlt },
];

// ─── Modal de Edição de Cliente — reutilizado nos filtros de Procurações,
// Vistoria e Despachante (ISSQN). Mesmo padrão do Requerimento DETRAN: nome,
// RG, CPF, CNPJ, telefone, e-mail e endereço com busca automática por CEP.
// ── CORREÇÃO: adicionado o campo Complemento (estado, payload de salvamento
// e input na tela), que faltava aqui mas já existia no cadastro original de
// clientes e no modal de Cadastrar Novo Cliente logo abaixo. ───────────────
function EditarClienteModal({ cliente, onSave, onClose, API_BASE_URL, addToast }) {
  const [form, setForm] = useState({
    nome: cliente.nome || '',
    cpf: cliente.cpf || '',
    cnpj: cliente.cnpj || '',
    identidade: cliente.identidade || '',
    telefone: cliente.telefone || '',
    email: cliente.email || '',
    logradouro: cliente.logradouro || '',
    numero: cliente.numero || '',
    bairro: cliente.bairro || '',
    complemento: cliente.complemento || '',
    cidade: cliente.cidade || '',
    uf: cliente.uf || '',
    cep: cliente.cep || '',
  });
  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  // ── CORREÇÃO: a validação por dígito verificador (checksum) bloqueava
  // documentos legítimos, antigos, que passaram a ser rejeitados mesmo tendo
  // a quantidade certa de dígitos (ex: CPF digitado há anos com um erro no
  // último dígito, mas que é o CPF real usado pelo cliente no sistema).
  // Agora a regra é a mesma da tela de Clientes: só confere a QUANTIDADE de
  // dígitos (11 pro CPF, 14 pro CNPJ) e se já existe outro cliente cadastrado
  // com o mesmo documento — sem validar o dígito verificador matemático. ──
  const [errors, setErrors] = useState({});

  const handle = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));

  // Print colado (Ctrl+V) com os dados do cliente: sobrescreve os campos lidos e destaca em amarelo os que mudaram.
  // Nada é salvo sozinho. O destaque some ao fechar o modal (o componente é desmontado).
  const [camposDoPrint, setCamposDoPrint] = useState([]);
  // CEP preenchido pelo print: enquanto não mudar, sair do campo CEP não busca o endereço na API
  const [cepDoPrint, setCepDoPrint] = useState('');
  const aplicarPrint = (dados, alterados) => {
    setForm(prev => ({ ...prev, ...dados }));
    setCamposDoPrint(prev => [...new Set([...prev, ...alterados])]);
    if (dados.cep) setCepDoPrint(dados.cep);
    setErrors(prev => { const e = { ...prev }; Object.keys(dados).forEach(campo => delete e[campo]); return e; });
  };
  const destaquePrint = (campo) => (camposDoPrint.includes(campo) ? ESTILO_CAMPO_DO_PRINT : {});

  const validar = () => {
    const e = {};
    if (!form.nome.trim() || form.nome.trim().length < 3) e.nome = 'Nome deve ter pelo menos 3 caracteres';
    const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
    const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
    if (cpfDigitos && cpfDigitos.length !== 11) e.cpf = 'CPF deve ter 11 dígitos';
    if (cnpjDigitos && cnpjDigitos.length !== 14) e.cnpj = 'CNPJ deve ter 14 dígitos';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buscarCep = async (cepValor) => {
    const c = (cepValor || '').replace(/\D/g, '');
    if (c.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await axios.get(`https://viacep.com.br/ws/${c}/json/`);
      if (res.data && !res.data.erro) {
        setForm(prev => ({
          ...prev,
          logradouro: res.data.logradouro || prev.logradouro,
          bairro: res.data.bairro || prev.bairro,
          cidade: res.data.localidade || prev.cidade,
          uf: res.data.uf || prev.uf,
        }));
        addToast('success', 'Endereço preenchido pelo CEP!');
      } else {
        addToast('warning', 'CEP não encontrado');
      }
    } catch {
      addToast('error', 'Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Lupa do CNPJ na edição: confere se o CNPJ não é de OUTRO cliente e completa só os campos em branco
  // (cadastro antigo/incompleto), sem trocar o que já está preenchido
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const podeConsultarCnpj = (form.cnpj || '').replace(/\D/g, '').length === 14 && !saving && !buscandoCnpj;

  const completarPorCnpj = async () => {
    if (!podeConsultarCnpj) return;
    const cnpjDigitos = form.cnpj.replace(/\D/g, '');
    const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } };
    setBuscandoCnpj(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpjDigitos}`, authHeader);
      if (res.data && res.data.idCliente !== cliente.idCliente) {
        setErrors(prev => ({ ...prev, cnpj: 'CNPJ já cadastrado para outro cliente' }));
        addToast('warning', 'Este CNPJ já está cadastrado para outro cliente');
        setBuscandoCnpj(false);
        return;
      }
    } catch {
      // 404 = não encontrado = não é duplicado, segue para a consulta
    }
    const { dados, erro } = await consultarCnpj(cnpjDigitos, API_BASE_URL, authHeader);
    setBuscandoCnpj(false);
    if (!dados) {
      addToast('error', erro);
      return;
    }
    const { novo, completados } = completarComDadosCnpj(form, dados);
    setForm(novo);
    setErrors(prev => { const e = { ...prev }; delete e.cnpj; return e; });
    const aviso = avisoCompletarCnpj(completados);
    addToast(aviso.tipo, aviso.texto);
  };

  const handleSalvar = async () => {
    if (!validar()) {
      addToast('warning', 'Corrija os erros antes de salvar');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('jwtToken');

      // ── NOVO: checagem de duplicidade — mesma regra da tela de Clientes.
      // Só bloqueia se o CPF/CNPJ já pertencer a OUTRO cliente (diferente
      // deste que está sendo editado); não bloqueia por dígito verificador. ──
      const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
      const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
      try {
        if (cpfDigitos.length === 11) {
          const res = await axios.get(`${API_BASE_URL}/clientes/cpf/${cpfDigitos}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data && res.data.idCliente !== cliente.idCliente) {
            setErrors(prev => ({ ...prev, cpf: 'CPF já cadastrado para outro cliente' }));
            addToast('warning', 'Este CPF já está cadastrado para outro cliente');
            setSaving(false);
            return;
          }
        }
        if (cnpjDigitos.length === 14) {
          const res = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpjDigitos}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data && res.data.idCliente !== cliente.idCliente) {
            setErrors(prev => ({ ...prev, cnpj: 'CNPJ já cadastrado para outro cliente' }));
            addToast('warning', 'Este CNPJ já está cadastrado para outro cliente');
            setSaving(false);
            return;
          }
        }
      } catch {
        // 404 = não encontrado = não é duplicado, segue normalmente
      }

      const payload = {
        nome: form.nome,
        cpf: form.cpf ? form.cpf.replace(/\D/g, '') : undefined,
        cnpj: form.cnpj ? form.cnpj.replace(/\D/g, '') : undefined,
        identidade: form.identidade || undefined,
        telefone: form.telefone ? form.telefone.replace(/\D/g, '') : undefined,
        email: form.email || undefined,
        logradouro: form.logradouro || undefined,
        numero: form.numero || undefined,
        bairro: form.bairro || undefined,
        complemento: form.complemento || undefined,
        cidade: form.cidade || undefined,
        uf: form.uf || undefined,
        cep: form.cep ? form.cep.replace(/\D/g, '') : undefined,
      };
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      const clienteAtualizado = { ...cliente, ...form };
      try {
        const response = await axios.put(`${API_BASE_URL}/clientes/${cliente.idCliente}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        if (response.data && Object.keys(response.data).length > 0) {
          Object.assign(clienteAtualizado, response.data);
        }
        addToast('success', 'Dados do cliente atualizados!');
      } catch (apiErr) {
        addToast('error', 'Erro ao salvar: ' + (apiErr.response?.data?.message || apiErr.message));
        setSaving(false);
        return;
      }
      onSave(clienteAtualizado);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={p.overlay} className="no-print" onClick={onClose}>
      <div style={p.editClienteModal} onClick={(e) => e.stopPropagation()}>
        <div style={p.modalConfigHeader}>
          <span style={p.modalConfigTitle}><FaEdit size={14} /> Editar Dados do Cliente</span>
          <button onClick={onClose} style={p.modalConfigClose}><FaTimes /></button>
        </div>
        <div style={p.editClienteBody}>
          <ImportarPrintCliente valoresAtuais={form} onDadosLidos={aplicarPrint} disabled={saving} />
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, flex: '1 1 100%' }}>
              <label style={p.editLabel}>Nome</label>
              <input value={form.nome} onChange={handle('nome')} style={{ ...p.editInput, ...destaquePrint('nome') }} />
              {errors.nome && <span style={p.editError}>{errors.nome}</span>}
            </div>
          </div>
          <div style={p.editRow}>
            <div style={p.editGroup}>
              <label style={p.editLabel}>CPF</label>
              <InputMask mask="999.999.999-99" value={form.cpf} onChange={handle('cpf')} placeholder="000.000.000-00">
                {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('cpf') }} />}
              </InputMask>
              {errors.cpf && <span style={p.editError}>{errors.cpf}</span>}
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>CNPJ</label>
              <div style={p.cnpjRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <InputMask mask="99.999.999/9999-99" value={form.cnpj} onChange={handle('cnpj')} placeholder="00.000.000/0000-00">
                    {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('cnpj') }} />}
                  </InputMask>
                </div>
                <button
                  type="button"
                  onClick={completarPorCnpj}
                  disabled={!podeConsultarCnpj}
                  title={podeConsultarCnpj ? 'Completar os campos em branco com os dados do CNPJ (Receita Federal)' : 'Digite um CNPJ completo para buscar os dados'}
                  style={{ ...p.cnpjButton, ...(!podeConsultarCnpj && p.cnpjButtonDisabled) }}
                >
                  <FaSearch />
                </button>
              </div>
              {buscandoCnpj && <span style={p.editHint}>Consultando CNPJ...</span>}
              {errors.cnpj && <span style={p.editError}>{errors.cnpj}</span>}
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>RG / Identidade</label>
              <input value={form.identidade} onChange={handle('identidade')} style={p.editInput} />
            </div>
          </div>
          <div style={p.editRow}>
            <div style={p.editGroup}>
              <label style={p.editLabel}>Telefone</label>
              <InputMask mask="(99) 99999-9999" maskChar={null} value={form.telefone} onChange={handle('telefone')} placeholder="(00) 00000-0000">
                {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('telefone') }} />}
              </InputMask>
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>E-mail</label>
              <input value={form.email} onChange={handle('email')} style={{ ...p.editInput, ...destaquePrint('email') }} />
            </div>
          </div>
          <div style={p.editSectionLabel}>Endereço</div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, maxWidth: 160 }}>
              <label style={p.editLabel}>CEP</label>
              <InputMask mask="99999-999" value={form.cep} onChange={handle('cep')} onBlur={(e) => { if (cepVeioDoPrint(e.target.value, cepDoPrint)) return; buscarCep(e.target.value); }} placeholder="00000-000">
                {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('cep') }} />}
              </InputMask>
              {loadingCep && <span style={p.editHint}>Buscando endereço...</span>}
            </div>
          </div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, flex: '1 1 100%' }}>
              <label style={p.editLabel}>Logradouro</label>
              <input value={form.logradouro} onChange={handle('logradouro')} style={{ ...p.editInput, ...destaquePrint('logradouro') }} />
            </div>
          </div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, maxWidth: 90 }}>
              <label style={p.editLabel}>Número</label>
              <input value={form.numero} onChange={handle('numero')} style={{ ...p.editInput, ...destaquePrint('numero') }} />
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>Bairro</label>
              <input value={form.bairro} onChange={handle('bairro')} style={{ ...p.editInput, ...destaquePrint('bairro') }} />
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>Cidade</label>
              <input value={form.cidade} onChange={handle('cidade')} style={{ ...p.editInput, ...destaquePrint('cidade') }} />
            </div>
            <div style={{ ...p.editGroup, maxWidth: 60 }}>
              <label style={p.editLabel}>UF</label>
              <input value={form.uf} onChange={handle('uf')} maxLength={2} style={{ ...p.editInput, ...destaquePrint('uf') }} />
            </div>
          </div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, flex: '1 1 100%' }}>
              <label style={p.editLabel}>Complemento</label>
              <input value={form.complemento} onChange={handle('complemento')} placeholder="Apartamento, bloco, sala..." style={{ ...p.editInput, ...destaquePrint('complemento') }} />
            </div>
          </div>
        </div>
        <div style={p.modalConfigFooter}>
          <button onClick={onClose} style={p.btnSecondaryConfig}>Cancelar</button>
          <button onClick={handleSalvar} style={p.btnAdd} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Alterações'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de Cadastro de Novo Cliente — aparece quando a busca por nome não
// encontra ninguém, nos 3 modais de seleção (Procurações, Vistoria,
// Despachante). Mesmos campos do cadastro completo, com máscaras de
// CPF/CNPJ/telefone/CEP e busca automática de endereço via ViaCEP. ───
function CadastrarClienteModal({ onSalvo, onClose, API_BASE_URL, addToast }) {
  const [form, setForm] = useState({
    nome: '', identidade: '', cpf: '', cnpj: '', telefone: '', email: '',
    logradouro: '', numero: '', bairro: '', complemento: '', cep: '', cidade: '', uf: '',
  });
  const [saving, setSaving] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  const [errors, setErrors] = useState({});
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);

  const handle = (campo) => (e) => setForm(prev => ({ ...prev, [campo]: e.target.value }));

  // Print colado (Ctrl+V) com os dados do cliente: sobrescreve os campos lidos e destaca em amarelo os que mudaram.
  // Nada é salvo sozinho. O destaque some ao fechar o modal (o componente é desmontado).
  const [camposDoPrint, setCamposDoPrint] = useState([]);
  // CEP preenchido pelo print: enquanto não mudar, sair do campo CEP não busca o endereço na API
  const [cepDoPrint, setCepDoPrint] = useState('');
  const aplicarPrint = (dados, alterados) => {
    setForm(prev => ({ ...prev, ...dados }));
    setCamposDoPrint(prev => [...new Set([...prev, ...alterados])]);
    if (dados.cep) setCepDoPrint(dados.cep);
    setErrors(prev => { const e = { ...prev }; Object.keys(dados).forEach(campo => delete e[campo]); return e; });
  };
  const destaquePrint = (campo) => (camposDoPrint.includes(campo) ? ESTILO_CAMPO_DO_PRINT : {});

  // Lupa do CNPJ: habilita com os 14 dígitos; ao clicar, confere se o CNPJ já está cadastrado
  // e, se não estiver, preenche nome, contato e endereço pelos dados da Receita.
  const podeConsultarCnpj = (form.cnpj || '').replace(/\D/g, '').length === 14 && !saving && !buscandoCnpj;

  const preencherPorCnpj = async () => {
    if (!podeConsultarCnpj) return;
    const cnpjDigitos = form.cnpj.replace(/\D/g, '');
    const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } };
    setBuscandoCnpj(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpjDigitos}`, authHeader);
      if (res.data) {
        setErrors(prev => ({ ...prev, cnpj: 'CNPJ já cadastrado' }));
        addToast('warning', 'Este CNPJ já está cadastrado');
        setBuscandoCnpj(false);
        return;
      }
    } catch {
      // 404 = não encontrado = não é duplicado, segue para a consulta
    }
    const { dados, erro } = await consultarCnpj(cnpjDigitos, API_BASE_URL, authHeader);
    setBuscandoCnpj(false);

    if (!dados) {
      addToast('error', erro);
      return;
    }

    // Substitui pelos dados do CNPJ (inclusive limpando o que não veio), para não sobrar dado de outro CNPJ
    setForm(prev => {
      const novo = { ...prev };
      Object.keys(dados).forEach(campo => {
        novo[campo] = dados[campo] || '';
      });
      return novo;
    });
    setErrors(prev => { const e = { ...prev }; delete e.cnpj; return e; });
    const camposOpcionais = {
      email: 'e-mail', telefone: 'telefone', cep: 'CEP', logradouro: 'endereço', bairro: 'bairro', cidade: 'cidade',
    };
    const faltando = Object.keys(camposOpcionais).filter(campo => !dados[campo]).map(campo => camposOpcionais[campo]);
    if (faltando.length > 0) {
      addToast('warning', `Dados preenchidos pelo CNPJ. Não encontrei: ${faltando.join(', ')}. Preencha manualmente.`);
    } else {
      addToast('success', 'Dados preenchidos a partir do CNPJ.');
    }
  };

  const buscarCep = async (cepValor) => {
    const c = (cepValor || '').replace(/\D/g, '');
    if (c.length !== 8) return;
    setLoadingCep(true);
    try {
      const res = await axios.get(`https://viacep.com.br/ws/${c}/json/`);
      if (res.data && !res.data.erro) {
        setForm(prev => ({
          ...prev,
          logradouro: res.data.logradouro || prev.logradouro,
          bairro: res.data.bairro || prev.bairro,
          cidade: res.data.localidade || prev.cidade,
          uf: res.data.uf || prev.uf,
        }));
        addToast('success', 'Endereço preenchido pelo CEP!');
      } else {
        addToast('warning', 'CEP não encontrado');
      }
    } catch {
      addToast('error', 'Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // ── CORREÇÃO: sem checagem de dígito verificador — só confere a
  // quantidade de dígitos (11 pro CPF, 14 pro CNPJ), igual à tela de
  // Clientes. A duplicidade é conferida à parte, no handleSalvar. ──
  const validar = () => {
    const e = {};
    if (!form.nome.trim() || form.nome.trim().length < 3) e.nome = 'Nome deve ter pelo menos 3 caracteres';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
    const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
    const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
    if (cpfDigitos && cpfDigitos.length !== 11) e.cpf = 'CPF deve ter 11 dígitos';
    if (cnpjDigitos && cnpjDigitos.length !== 14) e.cnpj = 'CNPJ deve ter 14 dígitos';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSalvar = async () => {
    if (!validar()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('jwtToken');

      // ── NOVO: checagem de duplicidade — mesma regra da tela de Clientes.
      // Ao cadastrar, qualquer CPF/CNPJ já existente é bloqueado. ──
      const cpfDigitos = form.cpf ? form.cpf.replace(/\D/g, '') : '';
      const cnpjDigitos = form.cnpj ? form.cnpj.replace(/\D/g, '') : '';
      try {
        if (cpfDigitos.length === 11) {
          const res = await axios.get(`${API_BASE_URL}/clientes/cpf/${cpfDigitos}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data) {
            setErrors(prev => ({ ...prev, cpf: 'CPF já cadastrado' }));
            addToast('warning', 'Este CPF já está cadastrado');
            setSaving(false);
            return;
          }
        }
        if (cnpjDigitos.length === 14) {
          const res = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpjDigitos}`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.data) {
            setErrors(prev => ({ ...prev, cnpj: 'CNPJ já cadastrado' }));
            addToast('warning', 'Este CNPJ já está cadastrado');
            setSaving(false);
            return;
          }
        }
      } catch {
        // 404 = não encontrado = não é duplicado, segue normalmente
      }

      const payload = {
        nome: form.nome,
        identidade: form.identidade || null,
        cpf: form.cpf ? form.cpf.replace(/\D/g, '') : null,
        cnpj: form.cnpj ? form.cnpj.replace(/\D/g, '') : null,
        telefone: form.telefone ? form.telefone.replace(/\D/g, '') : null,
        email: form.email || null,
        logradouro: form.logradouro || null,
        numero: form.numero || null,
        bairro: form.bairro || null,
        complemento: form.complemento || null,
        cep: form.cep ? form.cep.replace(/\D/g, '') : null,
        cidade: form.cidade || null,
        uf: form.uf || null,
      };
      const response = await axios.post(`${API_BASE_URL}/clientes`, payload, { headers: { Authorization: `Bearer ${token}` } });
      addToast('success', 'Cliente cadastrado com sucesso!');
      onSalvo(response.data || payload);
    } catch (err) {
      addToast('error', 'Erro ao cadastrar cliente: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={p.overlay} className="no-print" onClick={onClose}>
      <div style={p.editClienteModal} onClick={(e) => e.stopPropagation()}>
        <div style={p.modalConfigHeader}>
          <span style={p.modalConfigTitle}><FaPlus size={13} /> Cadastrar Novo Cliente</span>
          <button onClick={onClose} style={p.modalConfigClose}><FaTimes /></button>
        </div>
        <div style={p.editClienteBody}>
          <ImportarPrintCliente valoresAtuais={form} onDadosLidos={aplicarPrint} disabled={saving} />
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, flex: '1 1 100%' }}>
              <label style={p.editLabel}>Nome *</label>
              <input value={form.nome} onChange={handle('nome')} style={{ ...p.editInput, ...destaquePrint('nome') }} autoFocus />
              {errors.nome && <span style={p.editError}>{errors.nome}</span>}
            </div>
          </div>
          <div style={p.editRow}>
            <div style={p.editGroup}>
              <label style={p.editLabel}>RG / Identidade</label>
              <input value={form.identidade} onChange={(e) => setForm(prev => ({ ...prev, identidade: e.target.value.toUpperCase() }))} style={p.editInput} />
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>E-mail</label>
              <input value={form.email} onChange={handle('email')} style={{ ...p.editInput, ...destaquePrint('email') }} />
              {errors.email && <span style={p.editError}>{errors.email}</span>}
            </div>
          </div>
          <div style={p.editRow}>
            <div style={p.editGroup}>
              <label style={p.editLabel}>CPF</label>
              <InputMask mask="999.999.999-99" value={form.cpf} onChange={handle('cpf')} placeholder="000.000.000-00">
                {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('cpf') }} />}
              </InputMask>
              {errors.cpf && <span style={p.editError}>{errors.cpf}</span>}
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>CNPJ</label>
              <div style={p.cnpjRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <InputMask mask="99.999.999/9999-99" value={form.cnpj} onChange={handle('cnpj')} placeholder="00.000.000/0000-00">
                    {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('cnpj') }} />}
                  </InputMask>
                </div>
                <button
                  type="button"
                  onClick={preencherPorCnpj}
                  disabled={!podeConsultarCnpj}
                  title={podeConsultarCnpj ? 'Buscar dados do CNPJ (Receita Federal)' : 'Digite um CNPJ completo para buscar os dados'}
                  style={{ ...p.cnpjButton, ...(!podeConsultarCnpj && p.cnpjButtonDisabled) }}
                >
                  <FaSearch />
                </button>
              </div>
              {buscandoCnpj && <span style={p.editHint}>Consultando CNPJ...</span>}
              {errors.cnpj && <span style={p.editError}>{errors.cnpj}</span>}
            </div>
          </div>
          <div style={p.editRow}>
            <div style={p.editGroup}>
              <label style={p.editLabel}>Telefone</label>
              <InputMask mask="(99) 99999-9999" maskChar={null} value={form.telefone} onChange={handle('telefone')} placeholder="(00) 00000-0000">
                {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('telefone') }} />}
              </InputMask>
            </div>
          </div>
          <div style={p.editSectionLabel}>Endereço</div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, maxWidth: 160 }}>
              <label style={p.editLabel}>CEP</label>
              <InputMask mask="99999-999" value={form.cep} onChange={handle('cep')} onBlur={(e) => { if (cepVeioDoPrint(e.target.value, cepDoPrint)) return; buscarCep(e.target.value); }} placeholder="00000-000">
                {(inputProps) => <input {...inputProps} style={{ ...p.editInput, ...destaquePrint('cep') }} />}
              </InputMask>
              {loadingCep && <span style={p.editHint}>Buscando endereço...</span>}
            </div>
          </div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, flex: '1 1 100%' }}>
              <label style={p.editLabel}>Logradouro</label>
              <input value={form.logradouro} onChange={handle('logradouro')} style={{ ...p.editInput, ...destaquePrint('logradouro') }} />
            </div>
          </div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, maxWidth: 90 }}>
              <label style={p.editLabel}>Número</label>
              <input value={form.numero} onChange={handle('numero')} style={{ ...p.editInput, ...destaquePrint('numero') }} />
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>Bairro</label>
              <input value={form.bairro} onChange={handle('bairro')} style={{ ...p.editInput, ...destaquePrint('bairro') }} />
            </div>
            <div style={p.editGroup}>
              <label style={p.editLabel}>Cidade</label>
              <input value={form.cidade} onChange={handle('cidade')} style={{ ...p.editInput, ...destaquePrint('cidade') }} />
            </div>
            <div style={{ ...p.editGroup, maxWidth: 60 }}>
              <label style={p.editLabel}>UF</label>
              <input value={form.uf} onChange={(e) => setForm(prev => ({ ...prev, uf: e.target.value.toUpperCase() }))} maxLength={2} style={{ ...p.editInput, ...destaquePrint('uf') }} />
            </div>
          </div>
          <div style={p.editRow}>
            <div style={{ ...p.editGroup, flex: '1 1 100%' }}>
              <label style={p.editLabel}>Complemento</label>
              <input value={form.complemento} onChange={handle('complemento')} style={{ ...p.editInput, ...destaquePrint('complemento') }} />
            </div>
          </div>
        </div>
        <div style={p.modalConfigFooter}>
          <button onClick={onClose} style={p.btnSecondaryConfig}>Cancelar</button>
          <button onClick={handleSalvar} style={p.btnAdd} disabled={saving}>{saving ? 'Cadastrando...' : 'Cadastrar e Selecionar'}</button>
        </div>
      </div>
    </div>
  );
}

function PainelCRVA() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  // ── NOVO: cargo do usuário logado — usado para restringir, ao colaborador,
  // o acesso à engrenagem de configurações e aos atalhos de "Realizar saque"
  // (Pix e ISSQN). Gerente/Admin/Manager continuam com acesso integral. ──
  const [userRole, setUserRole] = useState('');
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token && !isExpired(token)) {
      const decoded = decodeToken(token);
      setUserRole((decoded?.tipoUsuario || '').toUpperCase());
    }
  }, []);
  const isColaborador = userRole === 'COLABORADOR' || userRole === 'FUNCIONARIO';

  const [abaAtiva, setAbaAtiva] = useState('crlv');
  const [dataSelecionada, setDataSelecionada] = useState(hojeISO());
  const [loading, setLoading] = useState(false);

  const [despachantes, setDespachantes] = useState([]);
  const [modalClienteDespachanteAberto, setModalClienteDespachanteAberto] = useState(false);
  const [buscaClienteDespachante, setBuscaClienteDespachante] = useState('');
  const [resultadosClienteDespachante, setResultadosClienteDespachante] = useState([]);
  const [buscandoClienteDespachante, setBuscandoClienteDespachante] = useState(false);

  const [modalEdicaoClienteAberto, setModalEdicaoClienteAberto] = useState(false);
  const [clienteEmEdicao, setClienteEmEdicao] = useState(null);

  const [modalCadastroClienteAberto, setModalCadastroClienteAberto] = useState(false);
  const [contextoCadastroCliente, setContextoCadastroCliente] = useState(null);

  const [crlvEntradas, setCrlvEntradas] = useState([]);
  const [crlvColunas, setCrlvColunas] = useState([]);
  const [celulaDraft, setCelulaDraft] = useState({});
  const criandoColunasIniciaisRef = useRef(false);
  const [crlvColunasCarregadas, setCrlvColunasCarregadas] = useState(false);

  const gridCellRefs = useRef({});
  const setGridCellRef = (colunaId, linha) => (el) => {
    gridCellRefs.current[`${colunaId}_${linha}`] = el;
  };

  const [selInicio, setSelInicio] = useState(null);
  const [selFim, setSelFim] = useState(null);
  const [selecionando, setSelecionando] = useState(false);

  useEffect(() => {
    const finalizarSelecao = () => setSelecionando(false);
    window.addEventListener('mouseup', finalizarSelecao);
    return () => window.removeEventListener('mouseup', finalizarSelecao);
  }, []);

  // ── Presença na grade do CRLV-e (como no Google Planilhas) ───────────────
  // Cada estação informa a célula que está com selecionada e se está digitando nela; as outras
  // estações destacam a célula com o nome da pessoa. Fica só na memória do servidor (some sozinho
  // se a estação parar de enviar). sessaoId é desta aba: o mesmo usuário em duas estações aparece nas duas.
  const sessaoCrlvIdRef = useRef(`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
  const presencaLocalCrlvRef = useRef({ colunaId: null, linha: null, digitando: false });
  const [presencasCrlv, setPresencasCrlv] = useState([]);

  const enviarPresencaCrlv = () => {
    const { colunaId, linha, digitando } = presencaLocalCrlvRef.current;
    axios.put(
      `${API_BASE_URL}/painel-crva/crlv/presenca`,
      { sessaoId: sessaoCrlvIdRef.current, colunaId, linha, digitando },
      { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } }
    ).catch(() => {}); // presença é só visual: falha não atrapalha a grade
  };

  const informarPresencaCrlv = (colunaId, linha, digitando) => {
    const atual = presencaLocalCrlvRef.current;
    if (atual.colunaId === colunaId && atual.linha === linha && atual.digitando === digitando) return;
    presencaLocalCrlvRef.current = { colunaId, linha, digitando };
    enviarPresencaCrlv();
  };

  // Saiu de uma célula: se o foco não foi para outra célula da grade, tira a marcação nas outras estações
  const sairDaCelulaCrlv = () => {
    setTimeout(() => {
      if (Object.values(gridCellRefs.current).includes(document.activeElement)) return;
      informarPresencaCrlv(null, null, false);
    }, 200);
  };

  // Presenças das outras estações por célula ("colunaId_linha" -> [presença])
  const presencasPorCelulaCrlv = presencasCrlv.reduce((mapa, pr) => {
    const chave = `${pr.colunaId}_${pr.linha}`;
    (mapa[chave] = mapa[chave] || []).push(pr);
    return mapa;
  }, {});

  const [procuracoes, setProcuracoes] = useState([]);
  const [novaProcuracao, setNovaProcuracao] = useState({ clienteId: '', clienteNome: '', placa: '' });
  const [buscaClienteProcuracao, setBuscaClienteProcuracao] = useState('');
  const [resultadosClienteProcuracao, setResultadosClienteProcuracao] = useState([]);
  const [buscandoClienteProcuracao, setBuscandoClienteProcuracao] = useState(false);
  const [modalClienteProcuracaoAberto, setModalClienteProcuracaoAberto] = useState(false);

  const [pixItens, setPixItens] = useState([]);
  const [valorProcuracaoUnitario, setValorProcuracaoUnitario] = useState(37.58);
  const [valorDescontoIssqnUnitario, setValorDescontoIssqnUnitario] = useState(0.40);
  const [novoPix, setNovoPix] = useState({ nome: '', valorRecebido: '', quantidadeProcuracao: '' });

  const [issqnAtivo, setIssqnAtivo] = useState(true);
  const [configIssqnAtivoInput, setConfigIssqnAtivoInput] = useState(true);

  const [issqnItens, setIssqnItens] = useState([]);

  const [vistoriaItens, setVistoriaItens] = useState([]);
  const [novaVistoria, setNovaVistoria] = useState({ clienteId: '', clienteNome: '', quantidade: '', valorUnitario: '', veiculo: '', local: '' });
  const [modalClienteVistoriaAberto, setModalClienteVistoriaAberto] = useState(false);
  const [buscaClienteVistoria, setBuscaClienteVistoria] = useState('');
  const [resultadosClienteVistoria, setResultadosClienteVistoria] = useState([]);
  const [buscandoClienteVistoria, setBuscandoClienteVistoria] = useState(false);
  const [vistoriaEditandoId, setVistoriaEditandoId] = useState(null);
  const [vistoriaEdicao, setVistoriaEdicao] = useState({ quantidade: '', valorUnitario: '', veiculo: '', local: '' });

  const [modalConfigAberto, setModalConfigAberto] = useState(false);
  const [configValorInput, setConfigValorInput] = useState('');
  const [configValorDescontoInput, setConfigValorDescontoInput] = useState('');
  const [salvandoConfig, setSalvandoConfig] = useState(false);

  const [modoPeriodo, setModoPeriodo] = useState(false);
  const [periodoInicio, setPeriodoInicio] = useState(hojeISO());
  const [periodoFim, setPeriodoFim] = useState(hojeISO());

  const [procuracaoEditandoId, setProcuracaoEditandoId] = useState(null);
  const [procuracaoEdicao, setProcuracaoEdicao] = useState({ placa: '' });

  const [pixEditandoId, setPixEditandoId] = useState(null);
  const [pixEdicao, setPixEdicao] = useState({ nome: '', valorRecebido: '', quantidadeProcuracao: '' });

  const [modalSaqueTipo, setModalSaqueTipo] = useState(null);
  const [registrandoSaque, setRegistrandoSaque] = useState(false);
  const [saquePixRealizadoHoje, setSaquePixRealizadoHoje] = useState(false);
  const [saqueIssqnRealizadoHoje, setSaqueIssqnRealizadoHoje] = useState(false);

  const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

  const carregarDespachantes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/painel-crva/despachantes`, getAuthHeader());
      setDespachantes(response.data || []);
    } catch {
      addToast('error', 'Erro ao carregar despachantes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  const buscarClientesDespachante = useCallback(async (termo) => {
    if (!termo || termo.trim().length < 2) { setResultadosClienteDespachante([]); return; }
    setBuscandoClienteDespachante(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/nome/${encodeURIComponent(termo.trim().toLowerCase())}`, getAuthHeader());
      const lista = Array.isArray(response.data) ? response.data : (response.data?.content || []);
      setResultadosClienteDespachante(lista);
    } catch (error) {
      const status = error.response?.status;
      setResultadosClienteDespachante([]);
      if (status !== 404 && status !== 204) {
        addToast('error', 'Erro ao buscar clientes: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setBuscandoClienteDespachante(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  useEffect(() => {
    const delay = setTimeout(() => buscarClientesDespachante(buscaClienteDespachante), 400);
    return () => clearTimeout(delay);
  }, [buscaClienteDespachante, buscarClientesDespachante]);

  const documentoCliente = (cliente) => (cliente?.cpf || cliente?.cnpj || '').replace(/\D/g, '');

  const [salvandoDespachanteCliente, setSalvandoDespachanteCliente] = useState(false);

  const selecionarClienteDespachante = async (cliente) => {
    if (salvandoDespachanteCliente) return;

    const docNovo = documentoCliente(cliente);
    const jaExiste = despachantes.some(d => {
      if (d.ativo === false) return false;
      if (d.clienteId === cliente.idCliente) return true;
      const docExistente = documentoCliente(d);
      return docNovo && docExistente && docNovo === docExistente;
    });
    if (jaExiste) {
      addToast('warning', `${cliente.nome} já está na lista de despachantes (mesmo CPF/CNPJ de um despachante já cadastrado)`);
      return;
    }
    setSalvandoDespachanteCliente(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/painel-crva/despachantes`, {
        clienteId: cliente.idCliente,
        nome: cliente.nome,
        cpf: cliente.cpf || null,
        cnpj: cliente.cnpj || null,
      }, getAuthHeader());
      const novo = {
        id: Date.now(),
        clienteId: cliente.idCliente,
        nome: cliente.nome,
        cpf: cliente.cpf || null,
        cnpj: cliente.cnpj || null,
        ativo: true,
        ...(response.data || {}),
      };
      setDespachantes(prev => [...prev, novo]);
      addToast('success', `${cliente.nome} adicionado como despachante!`);
    } catch (err) {
      addToast('error', 'Erro ao adicionar despachante: ' + (err.response?.data?.message || err.message));
    } finally {
      setSalvandoDespachanteCliente(false);
      setModalClienteDespachanteAberto(false);
      setBuscaClienteDespachante('');
      setResultadosClienteDespachante([]);
    }
  };

  const fecharModalClienteDespachante = () => {
    setModalClienteDespachanteAberto(false);
    setBuscaClienteDespachante('');
    setResultadosClienteDespachante([]);
  };

  const [despachanteParaDesativar, setDespachanteParaDesativar] = useState(null);
  const [desativandoDespachante, setDesativandoDespachante] = useState(false);

  const desativarDespachante = (despachante) => {
    setDespachanteParaDesativar(despachante);
  };

  const fecharConfirmacaoDesativarDespachante = () => {
    if (desativandoDespachante) return;
    setDespachanteParaDesativar(null);
  };

  const confirmarDesativarDespachante = async () => {
    if (!despachanteParaDesativar) return;
    setDesativandoDespachante(true);
    try {
      await axios.delete(`${API_BASE_URL}/painel-crva/despachantes/${despachanteParaDesativar.id}`, getAuthHeader());
      setDespachantes(prev => prev.map(d => d.id === despachanteParaDesativar.id ? { ...d, ativo: false } : d));
      addToast('success', 'Despachante desativado!');
      setDespachanteParaDesativar(null);
    } catch (err) {
      addToast('error', 'Erro ao desativar despachante: ' + (err.response?.data?.message || err.message));
    } finally {
      setDesativandoDespachante(false);
    }
  };

  const carregarConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/painel-crva/config`, getAuthHeader());
      if (response.data?.valorProcuracao) setValorProcuracaoUnitario(response.data.valorProcuracao);
      if (response.data?.valorDescontoIssqn !== undefined && response.data?.valorDescontoIssqn !== null) {
        setValorDescontoIssqnUnitario(response.data.valorDescontoIssqn);
      }
      if (response.data?.issqnAtivo !== undefined && response.data?.issqnAtivo !== null) {
        setIssqnAtivo(response.data.issqnAtivo);
      }
    } catch {
      // usa os defaults silenciosamente se o endpoint ainda não existir
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  const abrirModalConfig = () => {
    setConfigValorInput(String(valorProcuracaoUnitario).replace('.', ','));
    setConfigValorDescontoInput(String(valorDescontoIssqnUnitario).replace('.', ','));
    setConfigIssqnAtivoInput(issqnAtivo);
    setModalConfigAberto(true);
  };

  const salvarConfig = async () => {
    const valor = parseFloat(String(configValorInput).replace(',', '.'));
    const valorDesconto = parseFloat(String(configValorDescontoInput).replace(',', '.'));
    if (isNaN(valor) || valor <= 0) {
      addToast('warning', 'Informe um valor válido para a procuração');
      return;
    }
    if (isNaN(valorDesconto) || valorDesconto < 0) {
      addToast('warning', 'Informe um valor válido para o desconto do ISSQN');
      return;
    }
    setSalvandoConfig(true);
    try {
      await axios.put(`${API_BASE_URL}/painel-crva/config`, { valorProcuracao: valor, valorDescontoIssqn: valorDesconto, issqnAtivo: configIssqnAtivoInput }, getAuthHeader());
      setValorProcuracaoUnitario(valor);
      setValorDescontoIssqnUnitario(valorDesconto);
      setIssqnAtivo(configIssqnAtivoInput);
      if (!configIssqnAtivoInput && abaAtiva === 'issqn') setAbaAtiva('crlv');
      addToast('success', 'Configurações atualizadas!');
      setModalConfigAberto(false);
    } catch (err) {
      addToast('error', 'Erro ao salvar configuração: ' + (err.response?.data?.message || err.message));
    } finally {
      setSalvandoConfig(false);
    }
  };

  const carregarCrlvColunas = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/painel-crva/crlv-colunas`, getAuthHeader());
      setCrlvColunas(response.data || []);
    } catch {
      setCrlvColunas([]);
    } finally {
      setCrlvColunasCarregadas(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  const adicionarColunaCrlv = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/painel-crva/crlv-colunas`, { nome: '' }, getAuthHeader());
      setCrlvColunas(prev => [...prev, response.data]);
    } catch (err) {
      addToast('error', 'Erro ao adicionar coluna: ' + (err.response?.data?.message || err.message));
    }
  };

  const renomearColunaCrlv = (id, nome) => {
    setCrlvColunas(prev => prev.map(c => c.id === id ? { ...c, nome } : c));
  };

  const salvarNomeColunaCrlv = async (id, nome) => {
    try {
      await axios.put(`${API_BASE_URL}/painel-crva/crlv-colunas/${id}`, { nome }, getAuthHeader());
    } catch (err) {
      addToast('error', 'Erro ao salvar nome da coluna: ' + (err.response?.data?.message || err.message));
    }
  };

  const removerColunaCrlv = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/painel-crva/crlv-colunas/${id}`, getAuthHeader());
      setCrlvColunas(prev => prev.filter(c => c.id !== id));
      setCrlvEntradas(prev => prev.filter(e => e.colunaId !== id));
    } catch (err) {
      addToast('error', 'Erro ao remover coluna: ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Grade do CRLV-e compartilhada entre estações ──────────────────────────
  // Várias pessoas usam a mesma grade ao mesmo tempo (como uma planilha do Google): cada célula é gravada
  // pela posição (coluna + linha) - o backend substitui a placa da célula em vez de criar outro registro - e a
  // grade é recarregada sozinha a cada poucos segundos (atualizarCrlvSilencioso), pra uma estação ver o que a
  // outra digitou/apagou. Enquanto houver gravação desta estação em andamento, a recarga automática não
  // sobrescreve a grade (senão a célula "piscava" com o valor antigo).
  const opsCrlvPendentesRef = useRef(0);
  const versaoCrlvRef = useRef(0);
  const cabecalhoCrlvEmEdicaoRef = useRef(null);

  const executarOpCrlv = async (operacao) => {
    opsCrlvPendentesRef.current++;
    versaoCrlvRef.current++;
    try {
      return await operacao();
    } finally {
      opsCrlvPendentesRef.current--;
      versaoCrlvRef.current++;
    }
  };

  const mesmaCelula = (e, colunaId, linha) => e.colunaId === colunaId && e.linha === linha;

  const carregarCrlv = useCallback(async ({ silencioso = false } = {}) => {
    const versaoInicial = versaoCrlvRef.current;
    if (!silencioso) setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/painel-crva/crlv`, getAuthHeader());
      // Recarga automática: se esta estação gravou algo durante a busca, o retorno pode estar
      // desatualizado - descarta, a próxima recarga pega o estado certo.
      if (silencioso && (versaoInicial !== versaoCrlvRef.current || opsCrlvPendentesRef.current > 0)) return;
      const todas = response.data || [];
      const maisRecentePorCelula = new Map();
      todas
        .slice()
        .sort((a, b) => {
          const da = a.dataCadastro || a.data || '';
          const db = b.dataCadastro || b.data || '';
          if (da !== db) return da < db ? -1 : 1;
          return (a.id || 0) - (b.id || 0);
        })
        .forEach(e => maisRecentePorCelula.set(`${e.colunaId}_${e.linha}`, e));
      setCrlvEntradas(Array.from(maisRecentePorCelula.values()));
    } catch {
      if (!silencioso) setCrlvEntradas([]);
    } finally {
      if (!silencioso) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  // Nomes das colunas (clientes) também vêm das outras estações - menos o cabeçalho que está sendo editado aqui.
  const atualizarColunasCrlvSilencioso = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/painel-crva/crlv-colunas`, getAuthHeader());
      const colunas = response.data || [];
      if (!colunas.length) return;
      setCrlvColunas(prev => colunas.map(c => (
        c.id === cabecalhoCrlvEmEdicaoRef.current ? (prev.find(x => x.id === c.id) || c) : c
      )));
    } catch {
      // recarga automática: falha silenciosa, tenta de novo no próximo ciclo
    }
  };

  // Esvazia a célula pela posição (coluna + linha): apaga a placa e qualquer registro antigo da mesma
  // célula no banco, sem depender do id que esta estação conhece (pode ter sido trocado por outra estação).
  const limparCelulaCrlv = (colunaId, linha) => executarOpCrlv(async () => {
    setCrlvEntradas(prev => prev.filter(e => !mesmaCelula(e, colunaId, linha)));
    try {
      await axios.delete(`${API_BASE_URL}/painel-crva/crlv/celula?colunaId=${colunaId}&linha=${linha}`, getAuthHeader());
    } catch (err) {
      // a próxima recarga automática devolve a célula como está no banco
      addToast('error', 'Erro ao remover placa: ' + (err.response?.data?.message || err.message));
    }
  });

  useEffect(() => {
    if (modoPeriodo) return;
    if (!crlvColunasCarregadas) return;
    if (criandoColunasIniciaisRef.current) return;
    if (crlvColunas.length >= NUM_COLUNAS_CRLV) return;
    criandoColunasIniciaisRef.current = true;
    (async () => {
      const faltam = NUM_COLUNAS_CRLV - crlvColunas.length;
      for (let i = 0; i < faltam; i++) {
        // eslint-disable-next-line no-await-in-loop
        await adicionarColunaCrlv();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crlvColunas.length, modoPeriodo, crlvColunasCarregadas]);

  const celulaValor = (colunaId, linha) => {
    const chave = `${colunaId}_${linha}`;
    if (chave in celulaDraft) return celulaDraft[chave];
    const entrada = crlvEntradas.find(e => e.colunaId === colunaId && e.linha === linha);
    return entrada?.placa || '';
  };

  const celulaData = (colunaId, linha) => {
    const entrada = crlvEntradas.find(e => e.colunaId === colunaId && e.linha === linha);
    return entrada?.data || null;
  };

  const alterarCelula = (colunaId, linha, valor) => {
    setCelulaDraft(prev => ({ ...prev, [`${colunaId}_${linha}`]: valor.toUpperCase() }));
    informarPresencaCrlv(colunaId, linha, true);
  };

  const confirmarCelula = async (colunaId, linha, valorForcado) => {
    const chave = `${colunaId}_${linha}`;
    let valor;
    if (valorForcado !== undefined) {
      valor = valorForcado.trim();
    } else {
      if (!(chave in celulaDraft)) return;
      valor = celulaDraft[chave].trim();
    }
    const entrada = crlvEntradas.find(e => mesmaCelula(e, colunaId, linha));
    setCelulaDraft(prev => { const novo = { ...prev }; delete novo[chave]; return novo; });

    if (!valor) {
      if (entrada) await limparCelulaCrlv(colunaId, linha);
      return;
    }
    if (entrada && entrada.placa === valor) return;

    // Sempre grava pela posição: o backend substitui a placa se a célula já tiver registro
    // (inclusive um criado por outra estação que esta ainda não tinha recebido).
    await executarOpCrlv(async () => {
      const provisoria = entrada ? { ...entrada, placa: valor } : { id: `novo-${chave}`, colunaId, linha, placa: valor, data: hojeISO() };
      setCrlvEntradas(prev => [...prev.filter(e => !mesmaCelula(e, colunaId, linha)), provisoria]);
      try {
        const response = await axios.post(`${API_BASE_URL}/painel-crva/crlv`, { colunaId, linha, placa: valor, data: hojeISO() }, getAuthHeader());
        if (response.data) {
          setCrlvEntradas(prev => [...prev.filter(e => !mesmaCelula(e, colunaId, linha)), response.data]);
        }
      } catch (err) {
        addToast('error', 'Erro ao salvar placa: ' + (err.response?.data?.message || err.message));
      }
    });
  };

  const cancelarCelula = (colunaId, linha) => {
    const chave = `${colunaId}_${linha}`;
    setCelulaDraft(prev => {
      if (!(chave in prev)) return prev;
      const novo = { ...prev };
      delete novo[chave];
      return novo;
    });
  };

  const moverFocoGrid = (colIndex, rowIndex, deltaCol, deltaRow) => {
    const colunasVisiveis = crlvColunas.slice(0, NUM_COLUNAS_CRLV);
    let novoColIndex = colIndex + deltaCol;
    let novoRowIndex = rowIndex + deltaRow;

    if (novoColIndex < 0) novoColIndex = 0;
    if (novoColIndex > colunasVisiveis.length - 1) novoColIndex = colunasVisiveis.length - 1;
    if (novoRowIndex < 0) novoRowIndex = 0;
    if (novoRowIndex > NUM_LINHAS_CRLV - 1) novoRowIndex = NUM_LINHAS_CRLV - 1;

    const colunaAlvo = colunasVisiveis[novoColIndex];
    if (!colunaAlvo) return;

    const chave = `${colunaAlvo.id}_${novoRowIndex}`;
    const el = gridCellRefs.current[chave];
    if (el) {
      el.focus();
      requestAnimationFrame(() => el.select());
      setSelInicio({ colIndex: novoColIndex, linha: novoRowIndex });
      setSelFim({ colIndex: novoColIndex, linha: novoRowIndex });
    }
  };

  const limitesSelecao = () => {
    if (!selInicio || !selFim) return null;
    return {
      c0: Math.min(selInicio.colIndex, selFim.colIndex),
      c1: Math.max(selInicio.colIndex, selFim.colIndex),
      r0: Math.min(selInicio.linha, selFim.linha),
      r1: Math.max(selInicio.linha, selFim.linha),
    };
  };

  const isCelulaSelecionada = (colIndex, linha) => {
    const lim = limitesSelecao();
    if (!lim) return false;
    return colIndex >= lim.c0 && colIndex <= lim.c1 && linha >= lim.r0 && linha <= lim.r1;
  };

  // Resumo da seleção com mais de uma célula (barra abaixo da grade, como no Google Planilhas):
  // contagem das células preenchidas e, se houver números, soma e média deles.
  const resumoSelecaoCrlv = () => {
    const lim = limitesSelecao();
    if (!lim || (lim.c0 === lim.c1 && lim.r0 === lim.r1)) return null;
    const colunasVisiveis = crlvColunas.slice(0, NUM_COLUNAS_CRLV);
    let preenchidas = 0;
    const numeros = [];
    for (let ci = lim.c0; ci <= lim.c1; ci++) {
      const col = colunasVisiveis[ci];
      if (!col) continue;
      for (let li = lim.r0; li <= lim.r1; li++) {
        const valor = celulaValor(col.id, li).trim();
        if (!valor) continue;
        preenchidas++;
        const n = numeroDaCelula(valor);
        if (n !== null) numeros.push(n);
      }
    }
    const soma = numeros.reduce((s, n) => s + n, 0);
    return {
      celulas: (lim.c1 - lim.c0 + 1) * (lim.r1 - lim.r0 + 1),
      preenchidas,
      qtdNumeros: numeros.length,
      soma,
      media: numeros.length ? soma / numeros.length : 0,
    };
  };

  const isCelulaAncora = (colIndex, linha) =>
    selInicio && selInicio.colIndex === colIndex && selInicio.linha === linha;

  const handleCelulaMouseDown = (colIndex, linha) => (e) => {
    e.preventDefault();
    e.target.focus();
    setSelecionando(true);
    setSelInicio({ colIndex, linha });
    setSelFim({ colIndex, linha });
  };

  const handleCelulaMouseEnter = (colIndex, linha) => () => {
    if (selecionando) setSelFim({ colIndex, linha });
  };

  const limparSelecao = async () => {
    const lim = limitesSelecao();
    if (!lim) return;
    const colunasVisiveis = crlvColunas.slice(0, NUM_COLUNAS_CRLV);
    for (let ci = lim.c0; ci <= lim.c1; ci++) {
      const col = colunasVisiveis[ci];
      if (!col) continue;
      for (let li = lim.r0; li <= lim.r1; li++) {
        const chave = `${col.id}_${li}`;
        setCelulaDraft(prev => { const novo = { ...prev }; delete novo[chave]; return novo; });
        const entrada = crlvEntradas.find(e => mesmaCelula(e, col.id, li));
        if (entrada) {
          // eslint-disable-next-line no-await-in-loop
          await limparCelulaCrlv(col.id, li);
        }
      }
    }
  };

  const copiarSelecao = () => {
    const lim = limitesSelecao();
    if (!lim) return;
    const colunasVisiveis = crlvColunas.slice(0, NUM_COLUNAS_CRLV);
    const linhasTexto = [];
    for (let li = lim.r0; li <= lim.r1; li++) {
      const valoresLinha = [];
      for (let ci = lim.c0; ci <= lim.c1; ci++) {
        const col = colunasVisiveis[ci];
        valoresLinha.push(col ? celulaValor(col.id, li) : '');
      }
      linhasTexto.push(valoresLinha.join('\t'));
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(linhasTexto.join('\n')).catch(() => {});
    }
  };

  const handleGridPaste = (e, colIndexAncora, linhaAncora) => {
    const texto = e.clipboardData.getData('text');
    if (!texto || (!texto.includes('\t') && !texto.includes('\n'))) return;
    e.preventDefault();
    const colunasVisiveis = crlvColunas.slice(0, NUM_COLUNAS_CRLV);
    const linhas = texto.replace(/\r/g, '').split('\n');
    if (linhas.length > 1 && linhas[linhas.length - 1] === '') linhas.pop();
    linhas.forEach((linhaTexto, li) => {
      linhaTexto.split('\t').forEach((valor, ci) => {
        const col = colunasVisiveis[colIndexAncora + ci];
        const rowIndex = linhaAncora + li;
        if (!col || rowIndex >= NUM_LINHAS_CRLV) return;
        confirmarCelula(col.id, rowIndex, valor.trim().toUpperCase());
      });
    });
  };

  const handleGridKeyDown = (e, colId, colIndex, linha) => {
    const input = e.target;
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        moverFocoGrid(colIndex, linha, 0, -1);
        break;
      case 'ArrowDown':
      case 'Enter':
        e.preventDefault();
        moverFocoGrid(colIndex, linha, 0, 1);
        break;
      case 'ArrowLeft':
        if (input.selectionStart === 0 && input.selectionEnd === 0) {
          e.preventDefault();
          moverFocoGrid(colIndex, linha, -1, 0);
        }
        break;
      case 'ArrowRight':
        if (input.selectionStart === input.value.length && input.selectionEnd === input.value.length) {
          e.preventDefault();
          moverFocoGrid(colIndex, linha, 1, 0);
        }
        break;
      case 'Escape':
        e.preventDefault();
        cancelarCelula(colId, linha);
        input.blur();
        break;
      case 'Delete':
      case 'Backspace': {
        const lim = limitesSelecao();
        const multiplasCelulas = lim && (lim.c0 !== lim.c1 || lim.r0 !== lim.r1);
        if (multiplasCelulas) {
          e.preventDefault();
          limparSelecao();
        }
        break;
      }
      case 'c':
      case 'C':
        if (e.ctrlKey || e.metaKey) copiarSelecao();
        break;
      default:
        break;
    }
  };

  const carregarProcuracoes = useCallback(async () => {
    setLoading(true);
    try {
      const params = modoPeriodo ? { dataInicio: periodoInicio, dataFim: periodoFim } : { data: dataSelecionada };
      const response = await axios.get(`${API_BASE_URL}/painel-crva/procuracoes`, { ...getAuthHeader(), params });
      setProcuracoes(response.data || []);
    } catch {
      setProcuracoes([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, modoPeriodo, periodoInicio, periodoFim, dataSelecionada]);

  const buscarClientesProcuracao = useCallback(async (termo) => {
    if (!termo || termo.trim().length < 2) { setResultadosClienteProcuracao([]); return; }
    setBuscandoClienteProcuracao(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/nome/${encodeURIComponent(termo.trim().toLowerCase())}`, getAuthHeader());
      const lista = Array.isArray(response.data) ? response.data : (response.data?.content || []);
      setResultadosClienteProcuracao(lista);
    } catch (error) {
      const status = error.response?.status;
      setResultadosClienteProcuracao([]);
      if (status !== 404 && status !== 204) {
        console.error('Erro ao buscar clientes:', error);
        addToast('error', 'Erro ao buscar clientes: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setBuscandoClienteProcuracao(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  useEffect(() => {
    const delay = setTimeout(() => buscarClientesProcuracao(buscaClienteProcuracao), 400);
    return () => clearTimeout(delay);
  }, [buscaClienteProcuracao, buscarClientesProcuracao]);

  const selecionarClienteProcuracao = (cliente) => {
    setNovaProcuracao(prev => ({ ...prev, clienteId: cliente.idCliente, clienteNome: cliente.nome }));
    setBuscaClienteProcuracao('');
    setResultadosClienteProcuracao([]);
  };

  const limparClienteProcuracao = () => {
    setNovaProcuracao(prev => ({ ...prev, clienteId: '', clienteNome: '' }));
  };

  const fecharModalClienteProcuracao = () => {
    setModalClienteProcuracaoAberto(false);
    setBuscaClienteProcuracao('');
    setResultadosClienteProcuracao([]);
  };

  const adicionarProcuracao = async () => {
    if (!novaProcuracao.clienteId || !novaProcuracao.placa.trim()) {
      addToast('warning', 'Selecione o cliente e informe a placa');
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/painel-crva/procuracoes`, {
        clienteId: novaProcuracao.clienteId,
        placa: novaProcuracao.placa.trim().toUpperCase(),
        pix: false,
        emolumentoFeito: false,
        data: dataSelecionada,
      }, getAuthHeader());
      const item = response.data || {
        id: Date.now(),
        clienteId: novaProcuracao.clienteId,
        clienteNome: novaProcuracao.clienteNome,
        placa: novaProcuracao.placa.trim().toUpperCase(),
        pix: false,
        emolumentoFeito: false,
        dataCadastro: new Date().toISOString(),
      };
      setProcuracoes(prev => [...prev, item]);
      setNovaProcuracao({ clienteId: '', clienteNome: '', placa: '' });
    } catch (err) {
      addToast('error', 'Erro ao adicionar procuração: ' + (err.response?.data?.message || err.message));
    }
  };

  const atualizarProcuracao = async (id, patch) => {
    setProcuracoes(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
    try {
      await axios.put(`${API_BASE_URL}/painel-crva/procuracoes/${id}`, patch, getAuthHeader());
    } catch (err) {
      addToast('error', 'Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
  };

  const removerProcuracao = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/painel-crva/procuracoes/${id}`, getAuthHeader());
      setProcuracoes(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      addToast('error', 'Erro ao remover: ' + (err.response?.data?.message || err.message));
    }
  };

  const iniciarEdicaoProcuracao = (row) => {
    setProcuracaoEditandoId(row.id);
    setProcuracaoEdicao({ placa: row.placa });
  };
  const cancelarEdicaoProcuracao = () => { setProcuracaoEditandoId(null); };
  const salvarEdicaoProcuracao = async (id) => {
    const placa = procuracaoEdicao.placa.trim().toUpperCase();
    if (!placa) { addToast('warning', 'A placa não pode ficar em branco'); return; }
    await atualizarProcuracao(id, { placa });
    setProcuracaoEditandoId(null);
  };

  const carregarPix = useCallback(async () => {
    setLoading(true);
    try {
      const params = modoPeriodo ? { dataInicio: periodoInicio, dataFim: periodoFim } : { data: dataSelecionada };
      const response = await axios.get(`${API_BASE_URL}/painel-crva/pix`, { ...getAuthHeader(), params });
      setPixItens(response.data?.itens || response.data || []);
    } catch {
      setPixItens([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, modoPeriodo, periodoInicio, periodoFim, dataSelecionada]);

  const adicionarPix = async () => {
    const valor = parseFloat(novoPix.valorRecebido);
    if (!novoPix.nome.trim() || isNaN(valor) || valor <= 0) {
      addToast('warning', 'Informe o nome e o valor recebido');
      return;
    }
    const quantidadeProcuracao = novoPix.quantidadeProcuracao ? parseInt(novoPix.quantidadeProcuracao, 10) : 0;
    try {
      const response = await axios.post(`${API_BASE_URL}/painel-crva/pix`, {
        nome: novoPix.nome.trim().toUpperCase(),
        valorRecebido: valor,
        quantidadeProcuracao,
        data: dataSelecionada,
      }, getAuthHeader());
      const item = response.data || {
        id: Date.now(),
        nome: novoPix.nome.trim().toUpperCase(),
        valorRecebido: valor,
        quantidadeProcuracao,
        valorProcuracaoUnitario,
        dataCadastro: new Date().toISOString(),
      };
      setPixItens(prev => [...prev, item]);
      setNovoPix({ nome: '', valorRecebido: '', quantidadeProcuracao: '' });
    } catch (err) {
      addToast('error', 'Erro ao adicionar lançamento: ' + (err.response?.data?.message || err.message));
    }
  };

  const removerPix = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/painel-crva/pix/${id}`, getAuthHeader());
      setPixItens(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      addToast('error', 'Erro ao remover: ' + (err.response?.data?.message || err.message));
    }
  };

  const iniciarEdicaoPix = (row) => {
    setPixEditandoId(row.id);
    setPixEdicao({ nome: row.nome, valorRecebido: row.valorRecebido, quantidadeProcuracao: row.quantidadeProcuracao || '' });
  };
  const cancelarEdicaoPix = () => { setPixEditandoId(null); };
  const salvarEdicaoPix = async (id) => {
    const valor = parseFloat(pixEdicao.valorRecebido);
    if (!pixEdicao.nome.trim() || isNaN(valor) || valor <= 0) {
      addToast('warning', 'Informe o nome e o valor recebido');
      return;
    }
    const patch = {
      nome: pixEdicao.nome.trim().toUpperCase(),
      valorRecebido: valor,
      quantidadeProcuracao: pixEdicao.quantidadeProcuracao ? parseInt(pixEdicao.quantidadeProcuracao, 10) : 0,
    };
    setPixItens(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
    try {
      await axios.put(`${API_BASE_URL}/painel-crva/pix/${id}`, patch, getAuthHeader());
    } catch (err) {
      addToast('error', 'Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
    setPixEditandoId(null);
  };

  const atualizarConferenciaPix = async (id, conferido) => {
    setPixItens(prev => prev.map(p => p.id === id ? { ...p, conferido } : p));
    try {
      await axios.put(`${API_BASE_URL}/painel-crva/pix/${id}`, { conferido }, getAuthHeader());
    } catch (err) {
      setPixItens(prev => prev.map(p => p.id === id ? { ...p, conferido: !conferido } : p));
      addToast('error', 'Erro ao atualizar conferência: ' + (err.response?.data?.message || err.message));
    }
  };

  const pixComCalculo = pixItens.map(p => {
    const unitario = p.valorProcuracaoUnitario || valorProcuracaoUnitario;
    const qtd = p.quantidadeProcuracao || 0;
    const valorProcuracaoTotal = qtd > 0 ? qtd * unitario : 0;
    const saldoSobra = qtd > 0 ? (p.valorRecebido - valorProcuracaoTotal) : null;
    return { ...p, valorProcuracaoTotal, saldoSobra };
  });
  const totalRecebido = pixComCalculo.reduce((s, p) => s + (p.valorRecebido || 0), 0);
  const totalProcuracoes = pixComCalculo.reduce((s, p) => s + (p.valorProcuracaoTotal || 0), 0);
  const saldoSaquePix = totalRecebido - totalProcuracoes;
  const totalLancamentosPix = pixComCalculo.length;
  const totalQtdProcuracoesPix = pixComCalculo.reduce((s, p) => s + (p.quantidadeProcuracao || 0), 0);
  const totalPixConferidos = pixComCalculo.filter(p => p.conferido).length;

  const carregarIssqn = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const params = modoPeriodo ? { dataInicio: periodoInicio, dataFim: periodoFim } : { data: dataSelecionada };
      const response = await axios.get(`${API_BASE_URL}/painel-crva/issqn/automatico`, { ...getAuthHeader(), params });
      setIssqnItens(response.data || []);
    } catch {
      if (!silencioso) setIssqnItens([]);
    } finally {
      if (!silencioso) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, modoPeriodo, periodoInicio, periodoFim, dataSelecionada]);

  useEffect(() => {
    if (abaAtiva !== 'issqn') return;
    const intervalo = setInterval(() => {
      carregarIssqn(true);
    }, 30000);
    return () => clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, dataSelecionada, modoPeriodo, periodoInicio, periodoFim]);

  const issqnComCalculo = issqnItens.map(i => {
    const unitario = i.valorDescontoUnitario || valorDescontoIssqnUnitario;
    const valorDescontoTotal = i.valorDescontoTotal !== undefined && i.valorDescontoTotal !== null
      ? i.valorDescontoTotal
      : (i.quantidadeCrlv || 0) * unitario;
    return { ...i, valorDescontoTotal };
  });

  const totalIssqnQtd = issqnItens.reduce((s, i) => s + (i.quantidadeCrlv || 0), 0);
  const totalIssqnDesconto = issqnComCalculo.reduce((s, i) => s + (i.valorDescontoTotal || 0), 0);

  const nomeDespachante = (clienteId) => despachantes.find(d => d.clienteId === clienteId)?.nome || '—';

  const carregarVistoria = useCallback(async () => {
    setLoading(true);
    try {
      const params = modoPeriodo ? { dataInicio: periodoInicio, dataFim: periodoFim } : { data: dataSelecionada };
      const response = await axios.get(`${API_BASE_URL}/painel-crva/vistoria`, { ...getAuthHeader(), params });
      setVistoriaItens(response.data?.itens || response.data || []);
    } catch {
      setVistoriaItens([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, modoPeriodo, periodoInicio, periodoFim, dataSelecionada]);

  const buscarClientesVistoria = useCallback(async (termo) => {
    if (!termo || termo.trim().length < 2) { setResultadosClienteVistoria([]); return; }
    setBuscandoClienteVistoria(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/nome/${encodeURIComponent(termo.trim().toLowerCase())}`, getAuthHeader());
      const lista = Array.isArray(response.data) ? response.data : (response.data?.content || []);
      setResultadosClienteVistoria(lista);
    } catch (error) {
      const status = error.response?.status;
      setResultadosClienteVistoria([]);
      if (status !== 404 && status !== 204) {
        addToast('error', 'Erro ao buscar clientes: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setBuscandoClienteVistoria(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL]);

  useEffect(() => {
    const delay = setTimeout(() => buscarClientesVistoria(buscaClienteVistoria), 400);
    return () => clearTimeout(delay);
  }, [buscaClienteVistoria, buscarClientesVistoria]);

  const selecionarClienteVistoria = (cliente) => {
    setNovaVistoria(prev => ({ ...prev, clienteId: cliente.idCliente, clienteNome: cliente.nome }));
    setModalClienteVistoriaAberto(false);
    setBuscaClienteVistoria('');
    setResultadosClienteVistoria([]);
  };

  const limparClienteVistoria = () => {
    setNovaVistoria(prev => ({ ...prev, clienteId: '', clienteNome: '' }));
  };

  const fecharModalClienteVistoria = () => {
    setModalClienteVistoriaAberto(false);
    setBuscaClienteVistoria('');
    setResultadosClienteVistoria([]);
  };

  const adicionarVistoria = async () => {
    const qtd = parseInt(novaVistoria.quantidade, 10);
    const valorUnitario = parseFloat(String(novaVistoria.valorUnitario).replace(',', '.'));
    if (!novaVistoria.clienteId || isNaN(qtd) || qtd <= 0 || isNaN(valorUnitario) || valorUnitario < 0) {
      addToast('warning', 'Selecione o cliente e informe a quantidade e o valor unitário');
      return;
    }
    try {
      const response = await axios.post(`${API_BASE_URL}/painel-crva/vistoria`, {
        clienteId: novaVistoria.clienteId,
        clienteNome: novaVistoria.clienteNome,
        quantidade: qtd,
        valorUnitario,
        veiculo: novaVistoria.veiculo.trim().toUpperCase(),
        local: novaVistoria.local.trim(),
        data: dataSelecionada,
      }, getAuthHeader());
      const item = response.data || {
        id: Date.now(),
        clienteId: novaVistoria.clienteId,
        clienteNome: novaVistoria.clienteNome,
        quantidade: qtd,
        valorUnitario,
        veiculo: novaVistoria.veiculo.trim().toUpperCase(),
        local: novaVistoria.local.trim(),
        dataCadastro: new Date().toISOString(),
      };
      setVistoriaItens(prev => [...prev, item]);
      setNovaVistoria({ clienteId: '', clienteNome: '', quantidade: '', valorUnitario: '', veiculo: '', local: '' });
    } catch (err) {
      addToast('error', 'Erro ao adicionar: ' + (err.response?.data?.message || err.message));
    }
  };

  const removerVistoria = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/painel-crva/vistoria/${id}`, getAuthHeader());
      setVistoriaItens(prev => prev.filter(v => v.id !== id));
    } catch (err) {
      addToast('error', 'Erro ao remover: ' + (err.response?.data?.message || err.message));
    }
  };

  const iniciarEdicaoVistoria = (row) => {
    setVistoriaEditandoId(row.id);
    setVistoriaEdicao({
      quantidade: row.quantidade,
      valorUnitario: row.valorUnitario,
      veiculo: row.veiculo || '',
      local: row.local || '',
    });
  };
  const cancelarEdicaoVistoria = () => { setVistoriaEditandoId(null); };
  const salvarEdicaoVistoria = async (id) => {
    const qtd = parseInt(vistoriaEdicao.quantidade, 10);
    const valorUnitario = parseFloat(String(vistoriaEdicao.valorUnitario).replace(',', '.'));
    if (isNaN(qtd) || qtd <= 0 || isNaN(valorUnitario) || valorUnitario < 0) {
      addToast('warning', 'Informe a quantidade e o valor unitário');
      return;
    }
    const patch = {
      quantidade: qtd,
      valorUnitario,
      veiculo: vistoriaEdicao.veiculo.trim().toUpperCase(),
      local: vistoriaEdicao.local.trim(),
    };
    setVistoriaItens(prev => prev.map(v => v.id === id ? { ...v, ...patch } : v));
    try {
      await axios.put(`${API_BASE_URL}/painel-crva/vistoria/${id}`, patch, getAuthHeader());
    } catch (err) {
      addToast('error', 'Erro ao atualizar: ' + (err.response?.data?.message || err.message));
    }
    setVistoriaEditandoId(null);
  };

  const vistoriaComCalculo = vistoriaItens.map(v => ({
    ...v,
    valorTotal: (v.quantidade || 0) * (v.valorUnitario || 0),
  }));
  const totalVistoriaQtd = vistoriaItens.reduce((s, v) => s + (v.quantidade || 0), 0);
  const totalVistoriaValor = vistoriaComCalculo.reduce((s, v) => s + (v.valorTotal || 0), 0);

  const verificarSaqueRealizado = useCallback(async (tipo) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/saques/data/${dataSelecionada}/${dataSelecionada}/pagina?page=0&size=100`,
        getAuthHeader()
      );
      const marcador = tipo === 'pix' ? MARCADOR_SAQUE_PIX : MARCADOR_SAQUE_ISSQN;
      const encontrado = (response.data?.content || []).some(s => (s.descricao || '').includes(marcador));
      if (tipo === 'pix') setSaquePixRealizadoHoje(encontrado);
      else setSaqueIssqnRealizadoHoje(encontrado);
    } catch {
      if (tipo === 'pix') setSaquePixRealizadoHoje(false);
      else setSaqueIssqnRealizadoHoje(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE_URL, dataSelecionada]);

  const abrirModalSaque = (tipo) => setModalSaqueTipo(tipo);
  const fecharModalSaque = () => { if (!registrandoSaque) setModalSaqueTipo(null); };

  const confirmarSaqueAtalho = async () => {
    const tipo = modalSaqueTipo;
    const valor = tipo === 'pix' ? saldoSaquePix : totalIssqnDesconto;
    if (!valor || valor <= 0) {
      addToast('warning', 'Não há valor a sacar.');
      return;
    }
    const marcador = tipo === 'pix' ? MARCADOR_SAQUE_PIX : MARCADOR_SAQUE_ISSQN;
    const descricaoBase = tipo === 'pix'
      ? `Saque do saldo de Pix do dia ${formatarDataBR(dataSelecionada)}`
      : `Saque do desconto de ISSQN do dia ${formatarDataBR(dataSelecionada)}`;

    setRegistrandoSaque(true);
    try {
      await axios.post(`${API_BASE_URL}/saques`, {
        valor,
        descricao: `${marcador} ${descricaoBase}`,
        conta: { idConta: 1 },
      }, getAuthHeader());
      if (tipo === 'pix') setSaquePixRealizadoHoje(true);
      else setSaqueIssqnRealizadoHoje(true);
      addToast('success', `Saque de R$ ${formatarMoeda(valor)} registrado no caixa!`);
      setModalSaqueTipo(null);
    } catch (err) {
      addToast('error', 'Erro ao registrar saque: ' + (err.response?.data?.message || err.message));
    } finally {
      setRegistrandoSaque(false);
    }
  };

  useEffect(() => { carregarDespachantes(); carregarConfig(); carregarCrlvColunas(); }, [carregarDespachantes, carregarConfig, carregarCrlvColunas]);

  useEffect(() => {
    if (abaAtiva === 'crlv') carregarCrlv();
    else if (abaAtiva === 'procuracoes') carregarProcuracoes();
    else if (abaAtiva === 'pix') carregarPix();
    else if (abaAtiva === 'issqn') carregarIssqn();
    else if (abaAtiva === 'vistoria') carregarVistoria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, dataSelecionada, modoPeriodo, periodoInicio, periodoFim]);

  // Grade do CRLV-e: recarrega sozinha para mostrar o que as outras estações digitaram/apagaram
  // (a cada 5 s com a aba visível, e na hora ao voltar para a janela).
  useEffect(() => {
    if (abaAtiva !== 'crlv' || modoPeriodo) return undefined;
    const atualizar = () => {
      if (document.visibilityState !== 'visible') return;
      carregarCrlv({ silencioso: true });
      atualizarColunasCrlvSilencioso();
    };
    const intervalo = setInterval(atualizar, 5000);
    window.addEventListener('focus', atualizar);
    document.addEventListener('visibilitychange', atualizar);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener('focus', atualizar);
      document.removeEventListener('visibilitychange', atualizar);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, modoPeriodo, carregarCrlv]);

  // Presença: busca onde as outras estações estão (a cada 1,5 s) e reenvia a própria posição a cada 5 s
  // (o servidor descarta quem fica 15 s sem enviar). Ao sair da aba CRLV-e, tira a própria marcação.
  useEffect(() => {
    if (abaAtiva !== 'crlv' || modoPeriodo) return undefined;
    const buscarPresencas = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const response = await axios.get(
          `${API_BASE_URL}/painel-crva/crlv/presenca?sessaoId=${encodeURIComponent(sessaoCrlvIdRef.current)}`,
          getAuthHeader()
        );
        setPresencasCrlv(response.data || []);
      } catch {
        // só visual: mantém a última lista e tenta de novo no próximo ciclo
      }
    };
    const reenviarPosicao = () => {
      if (presencaLocalCrlvRef.current.colunaId !== null) enviarPresencaCrlv();
    };
    buscarPresencas();
    const intervaloBusca = setInterval(buscarPresencas, 1500);
    const intervaloEnvio = setInterval(reenviarPosicao, 5000);
    return () => {
      clearInterval(intervaloBusca);
      clearInterval(intervaloEnvio);
      setPresencasCrlv([]);
      presencaLocalCrlvRef.current = { colunaId: null, linha: null, digitando: false };
      enviarPresencaCrlv();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, modoPeriodo]);

  useEffect(() => {
    if (abaAtiva === 'crlv' && modoPeriodo) {
      setModoPeriodo(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva]);

  useEffect(() => {
    if (modoPeriodo || isColaborador) return;
    if (abaAtiva === 'pix') verificarSaqueRealizado('pix');
    else if (abaAtiva === 'issqn') verificarSaqueRealizado('issqn');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abaAtiva, dataSelecionada, modoPeriodo, isColaborador]);

  const ehRegistroDeHoje = (row) => {
    if (!row?.dataCadastro) return false;
    return row.dataCadastro.split('T')[0] === hojeISO();
  };

  const formatarDocumentoClienteModal = (cliente) => {
    const cpfDigitos = (cliente?.cpf || '').replace(/\D/g, '');
    const cnpjDigitos = (cliente?.cnpj || '').replace(/\D/g, '');
    if (cpfDigitos.length === 11) return `CPF: ${cliente.cpf}`;
    if (cnpjDigitos.length === 14) return `CNPJ: ${cliente.cnpj}`;
    return null;
  };

  const abrirEdicaoClienteModal = (cliente) => {
    setClienteEmEdicao(cliente);
    setModalEdicaoClienteAberto(true);
  };

  const handleClienteEditadoModal = (clienteAtualizado) => {
    const idAtualizado = clienteAtualizado.idCliente;
    setResultadosClienteProcuracao(prev => prev.map(c => c.idCliente === idAtualizado ? { ...c, ...clienteAtualizado } : c));
    setResultadosClienteVistoria(prev => prev.map(c => c.idCliente === idAtualizado ? { ...c, ...clienteAtualizado } : c));
    setResultadosClienteDespachante(prev => prev.map(c => c.idCliente === idAtualizado ? { ...c, ...clienteAtualizado } : c));
    setModalEdicaoClienteAberto(false);
    setClienteEmEdicao(null);
  };

  const abrirCadastroCliente = (contexto) => {
    setContextoCadastroCliente(contexto);
    setModalCadastroClienteAberto(true);
  };

  const handleClienteCadastradoModal = (novoCliente) => {
    const contexto = contextoCadastroCliente;
    setModalCadastroClienteAberto(false);
    setContextoCadastroCliente(null);
    if (contexto === 'procuracao') {
      selecionarClienteProcuracao(novoCliente);
      setModalClienteProcuracaoAberto(false);
    } else if (contexto === 'vistoria') {
      selecionarClienteVistoria(novoCliente);
    } else if (contexto === 'despachante') {
      selecionarClienteDespachante(novoCliente);
    }
  };

  const recarregarAtual = () => {
    if (abaAtiva === 'crlv') carregarCrlv();
    else if (abaAtiva === 'procuracoes') carregarProcuracoes();
    else if (abaAtiva === 'pix') carregarPix();
    else if (abaAtiva === 'issqn') carregarIssqn();
    else if (abaAtiva === 'vistoria') carregarVistoria();
  };

  return (
    <div style={p.page}>
      <div className="no-print"><Header /></div>
      <div style={p.wrap}>

        <div style={p.pageHeader} className="no-print">
          <div>
            <h1 style={p.title}>Painel CRVA</h1>
            <p style={p.subtitle}>Controle diário de CRLV-e, Procurações, Pix, ISSQN e Vistoria Fora da Sede</p>
          </div>
          <div style={p.headerActions}>
            {abaAtiva !== 'crlv' && (
              !modoPeriodo ? (
                <div style={p.dataBox}>
                  <FaCalendarAlt size={13} style={{ color: '#008080' }} />
                  <input type="date" value={dataSelecionada} onChange={(e) => setDataSelecionada(e.target.value)} style={p.dataInput} />
                  <button onClick={() => setDataSelecionada(hojeISO())} style={p.btnHoje}>Hoje</button>
                </div>
              ) : (
                <div style={p.dataBox}>
                  <FaCalendarAlt size={13} style={{ color: '#008080' }} />
                  <input type="date" value={periodoInicio} onChange={(e) => setPeriodoInicio(e.target.value)} style={p.dataInput} />
                  <span style={{ color: '#aaa', fontSize: 11 }}>até</span>
                  <input type="date" value={periodoFim} onChange={(e) => setPeriodoFim(e.target.value)} style={p.dataInput} />
                </div>
              )
            )}
            {abaAtiva !== 'crlv' && (
              <button onClick={() => setModoPeriodo(v => !v)} style={{ ...p.btnRecarregar, ...(modoPeriodo ? p.btnPeriodoAtivo : {}) }}>
                <FaSearch size={12} /> {modoPeriodo ? 'Voltar ao dia' : 'Buscar por período'}
              </button>
            )}
            <button onClick={recarregarAtual} style={p.btnRecarregar}><FaSync size={12} /> Recarregar</button>
            {abaAtiva !== 'crlv' && (
              <button
                onClick={() => window.print()}
                title={`Imprimir Relatório de ${ABAS.find(a => a.id === abaAtiva)?.label}`}
                style={p.btnImprimir}
              >
                <FaPrint size={12} style={{ flexShrink: 0 }} />
                <span style={p.btnImprimirTexto}>Imprimir Relatório de {ABAS.find(a => a.id === abaAtiva)?.label}</span>
              </button>
            )}
            {!isColaborador && (
              <button onClick={abrirModalConfig} style={p.btnConfig} title="Configurações"><FaCog size={14} /></button>
            )}
          </div>
        </div>

        <div style={p.tabsBar} className="no-print">
          {ABAS.filter(aba => aba.id !== 'issqn' || issqnAtivo).map(aba => {
            const Icon = aba.icon;
            return (
              <button key={aba.id} onClick={() => setAbaAtiva(aba.id)} style={{ ...p.tabBtn, ...(abaAtiva === aba.id ? p.tabBtnAtivo : {}) }}>
                <Icon size={13} /> {aba.label}
              </button>
            );
          })}
        </div>

        <div style={p.content} className="print-area">
          {abaAtiva !== 'crlv' && (
            <div className="print-only" style={p.printHeader}>
              <h2 style={p.printTitle}>Relatório de {ABAS.find(a => a.id === abaAtiva)?.label}</h2>
              <p style={p.printSubtitle}>
                {modoPeriodo ? `Período: ${formatarDataBR(periodoInicio)} até ${formatarDataBR(periodoFim)}` : `Data: ${formatarDataBR(dataSelecionada)}`}
              </p>
            </div>
          )}

          {abaAtiva === 'crlv' && (
            <div style={p.gridWrap}>
              <table style={p.gridTable}>
                <thead>
                  <tr>
                    {crlvColunas.slice(0, NUM_COLUNAS_CRLV).map(col => (
                      <th key={col.id} style={p.gridTh}>
                        <input
                          type="text"
                          value={col.nome}
                          onChange={(e) => renomearColunaCrlv(col.id, e.target.value.toUpperCase())}
                          onFocus={() => { cabecalhoCrlvEmEdicaoRef.current = col.id; }}
                          onBlur={(e) => {
                            // só libera a recarga automática desse cabeçalho depois de gravar o nome
                            salvarNomeColunaCrlv(col.id, e.target.value.toUpperCase()).finally(() => {
                              if (cabecalhoCrlvEmEdicaoRef.current === col.id) cabecalhoCrlvEmEdicaoRef.current = null;
                            });
                          }}
                          placeholder="Nome do cliente"
                          style={p.gridThInput}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: NUM_LINHAS_CRLV }).map((_, linha) => (
                    <tr key={linha}>
                      {crlvColunas.slice(0, NUM_COLUNAS_CRLV).map((col, colIndex) => {
                        // Outras estações nesta célula: borda na cor da pessoa + nome (e "digitando…")
                        const presencas = presencasPorCelulaCrlv[`${col.id}_${linha}`] || [];
                        const corPresenca = presencas.length ? corDaSessaoCrlv(presencas[0].sessaoId) : null;
                        return (
                          <td
                            key={col.id}
                            style={{
                              ...p.gridTd,
                              ...(isCelulaSelecionada(colIndex, linha) ? p.gridTdSelecionada : {}),
                              ...(isCelulaAncora(colIndex, linha) ? p.gridTdAncora : {}),
                              ...(corPresenca ? { position: 'relative', boxShadow: `inset 0 0 0 2px ${corPresenca}` } : {}),
                            }}
                          >
                            <input
                              ref={setGridCellRef(col.id, linha)}
                              type="text"
                              value={celulaValor(col.id, linha)}
                              onChange={(e) => alterarCelula(col.id, linha, e.target.value)}
                              onFocus={() => informarPresencaCrlv(col.id, linha, false)}
                              onBlur={() => { confirmarCelula(col.id, linha); sairDaCelulaCrlv(); }}
                              onKeyDown={(e) => handleGridKeyDown(e, col.id, colIndex, linha)}
                              onMouseDown={handleCelulaMouseDown(colIndex, linha)}
                              onMouseEnter={handleCelulaMouseEnter(colIndex, linha)}
                              onPaste={(e) => handleGridPaste(e, colIndex, linha)}
                              style={p.gridTdInput}
                            />
                            {corPresenca && (
                              <span
                                style={{ ...p.gridPresencaEtiqueta, background: corPresenca }}
                                title={presencas.map(pr => `${pr.nome}${pr.digitando ? ' (digitando…)' : ''}`).join(', ')}
                              >
                                {presencas.map(pr => primeiroNomeCrlv(pr.nome)).join(', ')}
                                {presencas.some(pr => pr.digitando) ? ' · digitando…' : ''}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {abaAtiva === 'crlv' && (() => {
            const resumo = resumoSelecaoCrlv();
            if (!resumo) return null;
            return (
              <div style={p.gridResumo} className="no-print">
                <span>{resumo.celulas} células selecionadas</span>
                <span>Contagem: <strong>{resumo.preenchidas}</strong></span>
                {resumo.qtdNumeros > 0 && (
                  <>
                    <span>Soma: <strong>{formatarNumeroBR(resumo.soma)}</strong></span>
                    <span>Média: <strong>{formatarNumeroBR(resumo.media)}</strong></span>
                  </>
                )}
              </div>
            );
          })()}

          {abaAtiva === 'procuracoes' && (
            <div>
              {!modoPeriodo && (
                <div style={p.formRow} className="no-print">
                  {novaProcuracao.clienteId ? (
                    <div style={p.clienteChipSelecionado}>
                      <FaUser size={11} />
                      <span>{novaProcuracao.clienteNome}</span>
                      <button onClick={limparClienteProcuracao} style={p.clienteChipRemove}><FaTimes size={10} /></button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setModalClienteProcuracaoAberto(true)}
                      style={p.btnSelecionarCliente}
                    >
                      <FaSearch size={12} /> Selecionar cliente
                    </button>
                  )}
                  <input type="text" value={novaProcuracao.placa} onChange={(e) => setNovaProcuracao(prev => ({ ...prev, placa: e.target.value.toUpperCase().slice(0, 7) }))} placeholder="Placa" maxLength={7} style={p.inputSmall} />
                  <button onClick={adicionarProcuracao} style={p.btnAdd}><FaPlus size={11} /> Adicionar</button>
                </div>
              )}
              {modoPeriodo && <p style={p.hintTexto} className="no-print">Exibindo registros de {periodoInicio.split('-').reverse().join('/')} até {periodoFim.split('-').reverse().join('/')}.</p>}

              <table style={p.table}>
                <thead>
                  <tr>
                    <th style={p.th}>Cliente</th>
                    <th style={p.th}>Placa</th>
                    <th style={p.th}>Pix</th>
                    <th style={p.th}>Emolumento</th>
                    <th style={p.th}>Registrado em</th>
                    <th style={{ ...p.th }} className="no-print"></th>
                  </tr>
                </thead>
                <tbody>
                  {procuracoes.map(row => (
                    <tr key={row.id}>
                      <td style={p.td}><span style={p.nomeClienteDestaque}>{row.clienteNome || `Cliente #${row.clienteId}`}</span></td>
                      <td style={p.td}>
                        {procuracaoEditandoId === row.id ? (
                          <input type="text" value={procuracaoEdicao.placa} onChange={(e) => setProcuracaoEdicao({ placa: e.target.value.toUpperCase().slice(0, 7) })} maxLength={7} style={p.inputInline} autoFocus />
                        ) : row.placa}
                      </td>
                      <td style={p.td}>
                        <input
                          type="checkbox"
                          checked={!!row.pix}
                          onChange={(e) => atualizarProcuracao(row.id, { pix: e.target.checked })}
                          style={p.checkbox}
                          disabled={!ehRegistroDeHoje(row)}
                          title={ehRegistroDeHoje(row) ? '' : 'Só é possível marcar itens do dia atual'}
                        />
                      </td>
                      <td style={p.td}>
                        <input
                          type="checkbox"
                          checked={!!row.emolumentoFeito}
                          onChange={(e) => atualizarProcuracao(row.id, { emolumentoFeito: e.target.checked })}
                          style={p.checkbox}
                          disabled={!ehRegistroDeHoje(row)}
                          title={ehRegistroDeHoje(row) ? '' : 'Só é possível marcar itens do dia atual'}
                        />
                      </td>
                      <td style={{ ...p.td, fontSize: 11, color: '#888' }}>{row.dataCadastro ? formatarDataHoraBR(row.dataCadastro) : '—'}</td>
                      <td style={p.td} className="no-print">
                        {!isColaborador && (
                          procuracaoEditandoId === row.id ? (
                            <span style={p.acoesInline}>
                              <button onClick={() => salvarEdicaoProcuracao(row.id)} style={p.btnSalvarInline}><FaCheck size={11} /></button>
                              <button onClick={cancelarEdicaoProcuracao} style={p.btnCancelarInline}><FaTimes size={11} /></button>
                            </span>
                          ) : (
                            <span style={p.acoesInline}>
                              <button
                                onClick={() => ehRegistroDeHoje(row) && iniciarEdicaoProcuracao(row)}
                                disabled={!ehRegistroDeHoje(row)}
                                style={{ ...p.btnEditInline, ...(ehRegistroDeHoje(row) ? {} : p.btnDesabilitado) }}
                                title={ehRegistroDeHoje(row) ? 'Editar' : 'Só é possível editar itens do dia atual'}
                              ><FaEdit size={11} /></button>
                              <button
                                onClick={() => ehRegistroDeHoje(row) && removerProcuracao(row.id)}
                                disabled={!ehRegistroDeHoje(row)}
                                style={{ ...p.btnDelete, ...(ehRegistroDeHoje(row) ? {} : p.btnDesabilitado) }}
                                title={ehRegistroDeHoje(row) ? 'Excluir' : 'Só é possível excluir itens do dia atual'}
                              ><FaTrash size={11} /></button>
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {procuracoes.length === 0 && <tr><td colSpan={6} style={p.emptyRow}>Nenhuma procuração lançada {modoPeriodo ? 'neste período' : 'nesta data'}</td></tr>}
                </tbody>
              </table>

              <div style={p.resumoCards}>
                <div style={p.resumoCard}>
                  <span style={p.resumoCardLabel}>Total</span>
                  <strong style={p.resumoCardQtd}>{procuracoes.length}</strong>
                  <span style={p.resumoCardValor}>R$ {formatarMoeda(procuracoes.length * valorProcuracaoUnitario)}</span>
                </div>
                <div style={p.resumoCard}>
                  <span style={p.resumoCardLabel}>Com Pix</span>
                  <strong style={p.resumoCardQtd}>{procuracoes.filter(r => r.pix).length}</strong>
                  <span style={p.resumoCardValor}>R$ {formatarMoeda(procuracoes.filter(r => r.pix).length * valorProcuracaoUnitario)}</span>
                </div>
                <div style={p.resumoCard}>
                  <span style={p.resumoCardLabel}>Sem Pix</span>
                  <strong style={p.resumoCardQtd}>{procuracoes.filter(r => !r.pix).length}</strong>
                  <span style={p.resumoCardValor}>R$ {formatarMoeda(procuracoes.filter(r => !r.pix).length * valorProcuracaoUnitario)}</span>
                </div>
              </div>
            </div>
          )}

          {abaAtiva === 'pix' && (
            <div>
              {!modoPeriodo && (
                <div style={p.formRow} className="no-print">
                  <input type="text" value={novoPix.nome} onChange={(e) => setNovoPix(prev => ({ ...prev, nome: e.target.value }))} placeholder="Nome" style={p.inputSmall} />
                  <input type="number" step="0.01" value={novoPix.valorRecebido} onChange={(e) => setNovoPix(prev => ({ ...prev, valorRecebido: e.target.value }))} placeholder="Valor recebido" style={p.inputSmall} />
                  <input type="number" value={novoPix.quantidadeProcuracao} onChange={(e) => setNovoPix(prev => ({ ...prev, quantidadeProcuracao: e.target.value }))} placeholder="Qtd. procuração (opcional)" style={p.inputSmall} />
                  <button onClick={adicionarPix} style={p.btnAdd}><FaPlus size={11} /> Adicionar</button>
                </div>
              )}
              {modoPeriodo && <p style={p.hintTexto} className="no-print">Exibindo registros de {periodoInicio.split('-').reverse().join('/')} até {periodoFim.split('-').reverse().join('/')}.</p>}

              <table style={p.table}>
                <thead>
                  <tr>
                    <th style={p.th}>Nome</th>
                    <th style={p.th}>Valor Recebido</th>
                    <th style={p.th}>Qtd. Procuração</th>
                    <th style={p.th}>Valor Procuração</th>
                    <th style={p.th}>Saldo/Sobra</th>
                    <th style={p.th}>Conferência na Conta</th>
                    <th style={p.th}>Registrado em</th>
                    <th style={p.th} className="no-print"></th>
                  </tr>
                </thead>
                <tbody>
                  {pixComCalculo.map(row => (
                    <tr key={row.id}>
                      {pixEditandoId === row.id ? (
                        <>
                          <td style={p.td}><input type="text" value={pixEdicao.nome} onChange={(e) => setPixEdicao(prev => ({ ...prev, nome: e.target.value }))} style={p.inputInline} autoFocus /></td>
                          <td style={p.td}><input type="number" step="0.01" value={pixEdicao.valorRecebido} onChange={(e) => setPixEdicao(prev => ({ ...prev, valorRecebido: e.target.value }))} style={p.inputInline} /></td>
                          <td style={p.td}><input type="number" value={pixEdicao.quantidadeProcuracao} onChange={(e) => setPixEdicao(prev => ({ ...prev, quantidadeProcuracao: e.target.value }))} style={p.inputInline} /></td>
                          <td style={p.td} colSpan={3}></td>
                        </>
                      ) : (
                        <>
                          <td style={p.td}><span style={p.nomeClienteDestaque}>{row.nome}</span></td>
                          <td style={p.td}>R$ {formatarMoeda(row.valorRecebido)}</td>
                          <td style={p.td}>{row.quantidadeProcuracao > 0 ? row.quantidadeProcuracao : '—'}</td>
                          <td style={p.td}>{row.quantidadeProcuracao > 0 ? `R$ ${formatarMoeda(row.valorProcuracaoTotal)}` : '—'}</td>
                          <td style={{ ...p.td, fontWeight: 700, color: row.saldoSobra !== null ? '#27ae60' : '#aaa' }}>{row.saldoSobra !== null ? `R$ ${formatarMoeda(row.saldoSobra)}` : '—'}</td>
                          <td style={p.td}>
                            <input
                              type="checkbox"
                              checked={!!row.conferido}
                              onChange={(e) => atualizarConferenciaPix(row.id, e.target.checked)}
                              style={p.checkbox}
                              title="Marcar quando conferir no extrato que o Pix entrou na conta"
                            />
                          </td>
                        </>
                      )}
                      <td style={{ ...p.td, fontSize: 11, color: '#888' }}>{row.dataCadastro ? formatarDataHoraBR(row.dataCadastro) : '—'}</td>
                      <td style={p.td} className="no-print">
                        {!isColaborador && (() => {
                          const podeEditar = ehRegistroDeHoje(row) && !saquePixRealizadoHoje;
                          const motivoBloqueio = !ehRegistroDeHoje(row)
                            ? 'Só é possível editar/excluir itens do dia atual'
                            : 'Saque do dia já realizado — edição bloqueada';
                          return pixEditandoId === row.id ? (
                            <span style={p.acoesInline}>
                              <button onClick={() => salvarEdicaoPix(row.id)} style={p.btnSalvarInline}><FaCheck size={11} /></button>
                              <button onClick={cancelarEdicaoPix} style={p.btnCancelarInline}><FaTimes size={11} /></button>
                            </span>
                          ) : (
                            <span style={p.acoesInline}>
                              <button
                                onClick={() => podeEditar && iniciarEdicaoPix(row)}
                                disabled={!podeEditar}
                                style={{ ...p.btnEditInline, ...(podeEditar ? {} : p.btnDesabilitado) }}
                                title={podeEditar ? 'Editar' : motivoBloqueio}
                              ><FaEdit size={11} /></button>
                              <button
                                onClick={() => podeEditar && removerPix(row.id)}
                                disabled={!podeEditar}
                                style={{ ...p.btnDelete, ...(podeEditar ? {} : p.btnDesabilitado) }}
                                title={podeEditar ? 'Excluir' : motivoBloqueio}
                              ><FaTrash size={11} /></button>
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                  {pixComCalculo.length === 0 && <tr><td colSpan={8} style={p.emptyRow}>Nenhum lançamento de Pix {modoPeriodo ? 'neste período' : 'nesta data'}</td></tr>}
                </tbody>
              </table>

              <div style={p.totaisPix}>
                <div style={p.totalCard}>
                  <span>TOTAL</span>
                  <strong>R$ {formatarMoeda(totalRecebido)}</strong>
                  <span style={p.totalCardQtd}>{totalLancamentosPix} lançamento{totalLancamentosPix === 1 ? '' : 's'}</span>
                </div>
                <div style={p.totalCard}>
                  <span>PROCURAÇÕES</span>
                  <strong>R$ {formatarMoeda(totalProcuracoes)}</strong>
                  <span style={p.totalCardQtd}>{totalQtdProcuracoesPix} procuraç{totalQtdProcuracoesPix === 1 ? 'ão' : 'ões'}</span>
                </div>
                <div style={{ ...p.totalCard, background: '#e8f8f0', borderColor: '#b2e8cc' }}>
                  <span>SALDO SAQUE PIX</span>
                  <strong style={{ color: '#27ae60' }}>R$ {formatarMoeda(saldoSaquePix)}</strong>
                  {!modoPeriodo && !isColaborador && (
                    <button
                      onClick={() => abrirModalSaque('pix')}
                      disabled={saquePixRealizadoHoje || saldoSaquePix <= 0}
                      style={{ ...p.btnRealizarSaque, ...((saquePixRealizadoHoje || saldoSaquePix <= 0) ? p.btnRealizarSaqueDisabled : {}) }}
                    >
                      <FaMoneyBillWave size={11} /> {saquePixRealizadoHoje ? 'Saque já realizado hoje' : 'Realizar saque deste valor'}
                    </button>
                  )}
                </div>
              </div>
              <p style={p.hintTexto}>Valor da procuração usado no cálculo: R$ {formatarMoeda(valorProcuracaoUnitario)} por unidade.</p>
              {totalLancamentosPix > 0 && (
                <p style={p.hintTexto}>
                  {totalPixConferidos} de {totalLancamentosPix} lançamento{totalLancamentosPix === 1 ? '' : 's'} conferido{totalPixConferidos === 1 ? '' : 's'} na conta.
                </p>
              )}
            </div>
          )}

          {abaAtiva === 'issqn' && (
            <div>
              <p style={p.hintTexto} className="no-print">
                {modoPeriodo
                  ? `Somando automaticamente as Ordens de Serviço de ${periodoInicio.split('-').reverse().join('/')} até ${periodoFim.split('-').reverse().join('/')}.`
                  : 'Atualizado automaticamente a partir das Ordens de Serviço do dia com o produto "Impressão de CRLV-E" — a cada 30 segundos, sozinho.'}
              </p>

              <table style={p.table}>
                <thead>
                  <tr>
                    <th style={p.th}>Despachante</th>
                    <th style={p.th}>Qtd. CRLV</th>
                    <th style={p.th}>Valor Total Desconto</th>
                  </tr>
                </thead>
                <tbody>
                  {issqnComCalculo.map(row => (
                    <tr key={row.clienteId}>
                      <td style={p.td}><span style={p.nomeClienteDestaque}>{row.clienteNome || nomeDespachante(row.clienteId)}</span></td>
                      <td style={p.td}>{row.quantidadeCrlv || 0}</td>
                      <td style={p.td}>R$ {formatarMoeda(row.valorDescontoTotal)}</td>
                    </tr>
                  ))}
                  {issqnComCalculo.length === 0 && (
                    <tr><td colSpan={3} style={p.emptyRow}>
                      Nenhum despachante cadastrado ainda. Adicione um pela engrenagem de Configurações.
                    </td></tr>
                  )}
                </tbody>
                {issqnComCalculo.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={p.tdTotal}>TOTAL</td>
                      <td style={p.tdTotal}>{totalIssqnQtd}</td>
                      <td style={p.tdTotal}>R$ {formatarMoeda(totalIssqnDesconto)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
              <p style={p.hintTexto}>Valor do desconto ISSQN usado no cálculo: R$ {formatarMoeda(valorDescontoIssqnUnitario)} por CRLV.</p>
              {!modoPeriodo && !isColaborador && totalIssqnDesconto > 0 && (
                <button
                  onClick={() => abrirModalSaque('issqn')}
                  disabled={saqueIssqnRealizadoHoje}
                  style={{ ...p.btnRealizarSaque, ...(saqueIssqnRealizadoHoje ? p.btnRealizarSaqueDisabled : {}), marginTop: 10 }}
                >
                  <FaMoneyBillWave size={11} /> {saqueIssqnRealizadoHoje ? 'Saque já realizado hoje' : 'Realizar saque do desconto ISSQN'}
                </button>
              )}
            </div>
          )}

          {abaAtiva === 'vistoria' && (
            <div>
              {!modoPeriodo && (
                <div style={p.formRow} className="no-print">
                  {novaVistoria.clienteId ? (
                    <div style={p.clienteChipSelecionado}>
                      <FaUser size={11} />
                      <span>{novaVistoria.clienteNome}</span>
                      <button onClick={limparClienteVistoria} style={p.clienteChipRemove}><FaTimes size={10} /></button>
                    </div>
                  ) : (
                    <button onClick={() => setModalClienteVistoriaAberto(true)} style={p.btnSelecionarCliente}>
                      <FaSearch size={12} /> Selecionar cliente
                    </button>
                  )}
                  <input type="number" value={novaVistoria.quantidade} onChange={(e) => setNovaVistoria(prev => ({ ...prev, quantidade: e.target.value }))} placeholder="Quantidade" style={p.inputSmall} />
                  <input type="text" value={novaVistoria.valorUnitario} onChange={(e) => setNovaVistoria(prev => ({ ...prev, valorUnitario: e.target.value }))} placeholder="Valor unitário (R$)" style={p.inputSmall} />
                  <input type="text" value={novaVistoria.veiculo} onChange={(e) => setNovaVistoria(prev => ({ ...prev, veiculo: e.target.value.toUpperCase() }))} placeholder="Veículo / Placa" style={p.inputSmall} />
                  <input type="text" value={novaVistoria.local} onChange={(e) => setNovaVistoria(prev => ({ ...prev, local: e.target.value }))} placeholder="Local" style={p.inputSmall} />
                  <button onClick={adicionarVistoria} style={p.btnAdd}><FaPlus size={11} /> Adicionar</button>
                </div>
              )}
              {modoPeriodo && <p style={p.hintTexto} className="no-print">Exibindo registros de {periodoInicio.split('-').reverse().join('/')} até {periodoFim.split('-').reverse().join('/')}.</p>}

              <table style={p.table}>
                <thead>
                  <tr>
                    <th style={p.th}>Cliente</th>
                    <th style={p.th}>Quantidade</th>
                    <th style={p.th}>Valor Unitário</th>
                    <th style={p.th}>Valor Total</th>
                    <th style={p.th}>Veículo</th>
                    <th style={p.th}>Local</th>
                    <th style={p.th}>Registrado em</th>
                    <th style={p.th} className="no-print"></th>
                  </tr>
                </thead>
                <tbody>
                  {vistoriaComCalculo.map(row => (
                    <tr key={row.id}>
                      <td style={p.td}><span style={p.nomeClienteDestaque}>{row.clienteNome}</span></td>
                      {vistoriaEditandoId === row.id ? (
                        <>
                          <td style={p.td}><input type="number" value={vistoriaEdicao.quantidade} onChange={(e) => setVistoriaEdicao(prev => ({ ...prev, quantidade: e.target.value }))} style={p.inputInline} autoFocus /></td>
                          <td style={p.td}><input type="text" value={vistoriaEdicao.valorUnitario} onChange={(e) => setVistoriaEdicao(prev => ({ ...prev, valorUnitario: e.target.value }))} style={p.inputInline} /></td>
                          <td style={p.td}></td>
                          <td style={p.td}><input type="text" value={vistoriaEdicao.veiculo} onChange={(e) => setVistoriaEdicao(prev => ({ ...prev, veiculo: e.target.value.toUpperCase() }))} style={p.inputInline} /></td>
                          <td style={p.td}><input type="text" value={vistoriaEdicao.local} onChange={(e) => setVistoriaEdicao(prev => ({ ...prev, local: e.target.value }))} style={p.inputInline} /></td>
                        </>
                      ) : (
                        <>
                          <td style={p.td}>{row.quantidade}</td>
                          <td style={p.td}>R$ {formatarMoeda(row.valorUnitario)}</td>
                          <td style={p.td}>R$ {formatarMoeda(row.valorTotal)}</td>
                          <td style={p.td}>{row.veiculo || '—'}</td>
                          <td style={p.td}>{row.local || '—'}</td>
                        </>
                      )}
                      <td style={{ ...p.td, fontSize: 11, color: '#888' }}>{row.dataCadastro ? formatarDataHoraBR(row.dataCadastro) : '—'}</td>
                      <td style={p.td} className="no-print">
                        {!isColaborador && (
                          vistoriaEditandoId === row.id ? (
                            <span style={p.acoesInline}>
                              <button onClick={() => salvarEdicaoVistoria(row.id)} style={p.btnSalvarInline}><FaCheck size={11} /></button>
                              <button onClick={cancelarEdicaoVistoria} style={p.btnCancelarInline}><FaTimes size={11} /></button>
                            </span>
                          ) : (
                            <span style={p.acoesInline}>
                              <button
                                onClick={() => ehRegistroDeHoje(row) && iniciarEdicaoVistoria(row)}
                                disabled={!ehRegistroDeHoje(row)}
                                style={{ ...p.btnEditInline, ...(ehRegistroDeHoje(row) ? {} : p.btnDesabilitado) }}
                                title={ehRegistroDeHoje(row) ? 'Editar' : 'Só é possível editar itens do dia atual'}
                              ><FaEdit size={11} /></button>
                              <button
                                onClick={() => ehRegistroDeHoje(row) && removerVistoria(row.id)}
                                disabled={!ehRegistroDeHoje(row)}
                                style={{ ...p.btnDelete, ...(ehRegistroDeHoje(row) ? {} : p.btnDesabilitado) }}
                                title={ehRegistroDeHoje(row) ? 'Excluir' : 'Só é possível excluir itens do dia atual'}
                              ><FaTrash size={11} /></button>
                            </span>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                  {vistoriaComCalculo.length === 0 && <tr><td colSpan={8} style={p.emptyRow}>Nenhuma vistoria fora da sede lançada {modoPeriodo ? 'neste período' : 'nesta data'}</td></tr>}
                </tbody>
                {vistoriaComCalculo.length > 0 && (
                  <tfoot>
                    <tr>
                      <td style={p.tdTotal}>TOTAL</td>
                      <td style={p.tdTotal}>{totalVistoriaQtd}</td>
                      <td style={p.tdTotal}></td>
                      <td style={p.tdTotal}>R$ {formatarMoeda(totalVistoriaValor)}</td>
                      <td style={p.tdTotal}></td>
                      <td style={p.tdTotal}></td>
                      <td style={p.tdTotal}></td>
                      <td style={p.tdTotal}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

        </div>
      </div>

      {modalConfigAberto && !isColaborador && (
        <div style={p.overlay} className="no-print">
          <div style={p.modalConfig}>
            <div style={p.modalConfigHeader}>
              <span style={p.modalConfigTitle}><FaCog size={14} /> Configurações</span>
              <button onClick={() => setModalConfigAberto(false)} style={p.modalConfigClose}><FaTimes /></button>
            </div>
            <div style={p.modalConfigBody}>
              <label style={p.labelConfig}>Valor da Certidão Validação da Procuração (R$)</label>
              <input
                type="text"
                value={configValorInput}
                onChange={(e) => setConfigValorInput(e.target.value)}
                placeholder="37,58"
                style={p.inputConfig}
                autoFocus
              />
              <label style={{ ...p.labelConfig, marginTop: 14 }}>Valor do Desconto ISSQN por Unidade (R$)</label>
              <input
                type="text"
                value={configValorDescontoInput}
                onChange={(e) => setConfigValorDescontoInput(e.target.value)}
                placeholder="0,40"
                style={p.inputConfig}
              />

              <label style={{ ...p.labelConfig, marginTop: 18 }}>Aba ISSQN</label>
              <div style={p.toggleRowConfig}>
                <span style={p.toggleRowConfigText}>Exibir a aba de ISSQN pra este cliente</span>
                <input
                  type="checkbox"
                  checked={configIssqnAtivoInput}
                  onChange={(e) => setConfigIssqnAtivoInput(e.target.checked)}
                  style={p.checkbox}
                />
              </div>

              <label style={{ ...p.labelConfig, marginTop: 18 }}>Despachantes</label>
              <p style={p.hintConfigDespachantes}>
                Despachante agora é um cliente já cadastrado no sistema — selecione-o abaixo e ele entra na lista.
              </p>
              <div style={p.despachantesListaConfig}>
                {despachantes.filter(d => d.ativo !== false).length === 0 && (
                  <p style={{ fontSize: 12, color: '#aaa', margin: '4px 0' }}>Nenhum despachante cadastrado ainda.</p>
                )}
                {despachantes.filter(d => d.ativo !== false).map(d => (
                  <div key={d.id} style={p.despachanteLinhaConfig}>
                    <span>{d.nome}</span>
                    <button onClick={() => desativarDespachante(d)} style={p.btnDesativarConfig} title="Desativar despachante">Desativar</button>
                  </div>
                ))}
              </div>
              <button onClick={() => setModalClienteDespachanteAberto(true)} style={p.btnSelecionarClienteDespachante}>
                <FaSearch size={11} /> Selecionar cliente para virar despachante
              </button>
            </div>
            <div style={p.modalConfigFooter}>
              <button onClick={() => setModalConfigAberto(false)} style={p.btnSecondaryConfig}>Cancelar</button>
              <button onClick={salvarConfig} style={p.btnAdd} disabled={salvandoConfig}>{salvandoConfig ? 'Salvando...' : 'Salvar'}</button>
            </div>
          </div>
        </div>
      )}

      {despachanteParaDesativar && (
        <div style={p.overlay} className="no-print" onClick={fecharConfirmacaoDesativarDespachante}>
          <div style={p.modalConfig} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...p.modalConfigHeader, background: 'linear-gradient(135deg, #b03a2e, #e74c3c)' }}>
              <span style={p.modalConfigTitle}><FaTrash size={13} /> Desativar despachante</span>
              <button onClick={fecharConfirmacaoDesativarDespachante} style={p.modalConfigClose}><FaTimes /></button>
            </div>
            <div style={p.modalConfigBody}>
              <p style={p.textoModalSaque}>
                Desativar <strong>"{despachanteParaDesativar.nome}"</strong>?
              </p>
              <p style={p.hintModalSaque}>
                Ele vai deixar de aparecer nas opções de novos lançamentos, mas o nome continua aparecendo
                normalmente nos registros já criados com ele.
              </p>
            </div>
            <div style={p.modalConfigFooter}>
              <button onClick={fecharConfirmacaoDesativarDespachante} style={p.btnSecondaryConfig} disabled={desativandoDespachante}>Cancelar</button>
              <button onClick={confirmarDesativarDespachante} style={p.btnDesativarModalConfirmar} disabled={desativandoDespachante}>
                {desativandoDespachante ? 'Desativando...' : 'Desativar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalSaqueTipo && !isColaborador && (
        <div style={p.overlay} className="no-print" onClick={fecharModalSaque}>
          <div style={p.modalConfig} onClick={(e) => e.stopPropagation()}>
            <div style={{ ...p.modalConfigHeader, background: 'linear-gradient(135deg, #1e8449, #27ae60)' }}>
              <span style={p.modalConfigTitle}><FaMoneyBillWave size={14} /> Confirmar saque</span>
              <button onClick={fecharModalSaque} style={p.modalConfigClose}><FaTimes /></button>
            </div>
            <div style={p.modalConfigBody}>
              {modalSaqueTipo === 'pix' ? (
                <p style={p.textoModalSaque}>
                  Realizar saque <strong>Pix</strong> com valor de{' '}
                  <strong style={{ color: '#27ae60' }}>R$ {formatarMoeda(saldoSaquePix)}</strong>, referente ao saldo
                  do dia {formatarDataBR(dataSelecionada)}?
                </p>
              ) : (
                <p style={p.textoModalSaque}>
                  Realizar saque <strong>ISSQN</strong> com valor de{' '}
                  <strong style={{ color: '#27ae60' }}>R$ {formatarMoeda(totalIssqnDesconto)}</strong>, referente ao
                  desconto do dia {formatarDataBR(dataSelecionada)}?
                </p>
              )}
              <p style={p.hintModalSaque}>
                Isso registra um saque no caixa (mesmo lançamento da tela de Saques) e desabilita esse atalho
                até o próximo dia.
              </p>
            </div>
            <div style={p.modalConfigFooter}>
              <button onClick={fecharModalSaque} style={p.btnSecondaryConfig} disabled={registrandoSaque}>Cancelar</button>
              <button onClick={confirmarSaqueAtalho} style={p.btnAdd} disabled={registrandoSaque}>
                {registrandoSaque ? 'Registrando...' : 'Confirmar saque'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalClienteProcuracaoAberto && (
        <div style={p.overlay} className="no-print" onClick={fecharModalClienteProcuracao}>
          <div style={p.modalCliente} onClick={(e) => e.stopPropagation()}>
            <div style={p.modalConfigHeader}>
              <span style={p.modalConfigTitle}><FaUser size={14} /> Selecionar cliente</span>
              <button onClick={fecharModalClienteProcuracao} style={p.modalConfigClose}><FaTimes /></button>
            </div>
            <div style={p.modalClienteBody}>
              <input
                type="text"
                value={buscaClienteProcuracao}
                onChange={(e) => setBuscaClienteProcuracao(e.target.value)}
                placeholder="Digite o nome do cliente..."
                style={p.inputBuscaModalCliente}
                autoFocus
              />
              <div style={p.clienteModalLista}>
                {buscandoClienteProcuracao && <div style={p.clienteSearchHint}>Buscando...</div>}
                {!buscandoClienteProcuracao && buscaClienteProcuracao.trim().length < 2 && (
                  <div style={p.clienteSearchHint}>Digite ao menos 2 letras para buscar</div>
                )}
                {!buscandoClienteProcuracao && buscaClienteProcuracao.trim().length >= 2 && resultadosClienteProcuracao.length === 0 && (
                  <div style={p.semResultadoBox}>
                    <span style={p.clienteSearchHint}>Nenhum cliente encontrado com esse nome</span>
                    <button onClick={() => abrirCadastroCliente('procuracao')} style={p.btnCadastrarNovoModal}>
                      <FaPlus size={11} /> Cadastrar novo cliente
                    </button>
                  </div>
                )}
                {!buscandoClienteProcuracao && resultadosClienteProcuracao.map(c => {
                  const doc = formatarDocumentoClienteModal(c);
                  const semDoc = !doc;
                  return (
                    <div key={c.idCliente} style={p.clienteModalItemLinha}>
                      <div style={p.clienteModalItemInfo}>
                        <span style={p.clienteModalItemNome}>{c.nome}</span>
                        {doc && <span style={p.docBadge}>{doc}</span>}
                        {semDoc && <span style={p.docBadgeAlerta}><FaExclamationTriangle size={9} /> CPF/CNPJ obrigatório</span>}
                      </div>
                      <div style={p.clienteModalAcoes}>
                        <button onClick={() => abrirEdicaoClienteModal(c)} style={p.btnEditarClienteModal} title="Editar dados do cliente">
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => { if (!semDoc) { selecionarClienteProcuracao(c); setModalClienteProcuracaoAberto(false); } }}
                          disabled={semDoc}
                          style={{ ...p.btnSelecionarClienteModal, ...(semDoc ? p.btnSelecionarClienteModalDisabled : {}) }}
                          title={semDoc ? 'Cadastre o CPF ou CNPJ deste cliente antes de selecionar' : undefined}
                        >
                          Selecionar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {modalClienteVistoriaAberto && (
        <div style={p.overlay} className="no-print" onClick={fecharModalClienteVistoria}>
          <div style={p.modalCliente} onClick={(e) => e.stopPropagation()}>
            <div style={p.modalConfigHeader}>
              <span style={p.modalConfigTitle}><FaUser size={14} /> Selecionar cliente</span>
              <button onClick={fecharModalClienteVistoria} style={p.modalConfigClose}><FaTimes /></button>
            </div>
            <div style={p.modalClienteBody}>
              <input
                type="text"
                value={buscaClienteVistoria}
                onChange={(e) => setBuscaClienteVistoria(e.target.value)}
                placeholder="Digite o nome do cliente..."
                style={p.inputBuscaModalCliente}
                autoFocus
              />
              <div style={p.clienteModalLista}>
                {buscandoClienteVistoria && <div style={p.clienteSearchHint}>Buscando...</div>}
                {!buscandoClienteVistoria && buscaClienteVistoria.trim().length < 2 && (
                  <div style={p.clienteSearchHint}>Digite ao menos 2 letras para buscar</div>
                )}
                {!buscandoClienteVistoria && buscaClienteVistoria.trim().length >= 2 && resultadosClienteVistoria.length === 0 && (
                  <div style={p.semResultadoBox}>
                    <span style={p.clienteSearchHint}>Nenhum cliente encontrado com esse nome</span>
                    <button onClick={() => abrirCadastroCliente('vistoria')} style={p.btnCadastrarNovoModal}>
                      <FaPlus size={11} /> Cadastrar novo cliente
                    </button>
                  </div>
                )}
                {!buscandoClienteVistoria && resultadosClienteVistoria.map(c => {
                  const doc = formatarDocumentoClienteModal(c);
                  const semDoc = !doc;
                  return (
                    <div key={c.idCliente} style={p.clienteModalItemLinha}>
                      <div style={p.clienteModalItemInfo}>
                        <span style={p.clienteModalItemNome}>{c.nome}</span>
                        {doc && <span style={p.docBadge}>{doc}</span>}
                        {semDoc && <span style={p.docBadgeAlerta}><FaExclamationTriangle size={9} /> CPF/CNPJ obrigatório</span>}
                      </div>
                      <div style={p.clienteModalAcoes}>
                        <button onClick={() => abrirEdicaoClienteModal(c)} style={p.btnEditarClienteModal} title="Editar dados do cliente">
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => { if (!semDoc) selecionarClienteVistoria(c); }}
                          disabled={semDoc}
                          style={{ ...p.btnSelecionarClienteModal, ...(semDoc ? p.btnSelecionarClienteModalDisabled : {}) }}
                          title={semDoc ? 'Cadastre o CPF ou CNPJ deste cliente antes de selecionar' : undefined}
                        >
                          Selecionar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {modalClienteDespachanteAberto && (
        <div style={p.overlay} className="no-print" onClick={fecharModalClienteDespachante}>
          <div style={p.modalCliente} onClick={(e) => e.stopPropagation()}>
            <div style={p.modalConfigHeader}>
              <span style={p.modalConfigTitle}><FaUser size={14} /> Selecionar cliente</span>
              <button onClick={fecharModalClienteDespachante} style={p.modalConfigClose}><FaTimes /></button>
            </div>
            <div style={p.modalClienteBody}>
              <input
                type="text"
                value={buscaClienteDespachante}
                onChange={(e) => setBuscaClienteDespachante(e.target.value)}
                placeholder="Digite o nome do cliente..."
                style={p.inputBuscaModalCliente}
                autoFocus
              />
              <div style={p.clienteModalLista}>
                {buscandoClienteDespachante && <div style={p.clienteSearchHint}>Buscando...</div>}
                {!buscandoClienteDespachante && buscaClienteDespachante.trim().length < 2 && (
                  <div style={p.clienteSearchHint}>Digite ao menos 2 letras para buscar</div>
                )}
                {!buscandoClienteDespachante && buscaClienteDespachante.trim().length >= 2 && resultadosClienteDespachante.length === 0 && (
                  <div style={p.semResultadoBox}>
                    <span style={p.clienteSearchHint}>Nenhum cliente encontrado com esse nome</span>
                    <button onClick={() => abrirCadastroCliente('despachante')} style={p.btnCadastrarNovoModal}>
                      <FaPlus size={11} /> Cadastrar novo cliente
                    </button>
                  </div>
                )}
                {!buscandoClienteDespachante && resultadosClienteDespachante.map(c => {
                  const doc = formatarDocumentoClienteModal(c);
                  const semDoc = !doc;
                  return (
                    <div key={c.idCliente} style={p.clienteModalItemLinha}>
                      <div style={p.clienteModalItemInfo}>
                        <span style={p.clienteModalItemNome}>{c.nome}</span>
                        {doc && <span style={p.docBadge}>{doc}</span>}
                        {semDoc && <span style={p.docBadgeAlerta}><FaExclamationTriangle size={9} /> CPF/CNPJ obrigatório</span>}
                      </div>
                      <div style={p.clienteModalAcoes}>
                        <button onClick={() => abrirEdicaoClienteModal(c)} style={p.btnEditarClienteModal} title="Editar dados do cliente">
                          <FaEdit size={11} />
                        </button>
                        <button
                          onClick={() => { if (!semDoc) selecionarClienteDespachante(c); }}
                          disabled={semDoc || salvandoDespachanteCliente}
                          style={{ ...p.btnSelecionarClienteModal, ...((semDoc || salvandoDespachanteCliente) ? p.btnSelecionarClienteModalDisabled : {}) }}
                          title={semDoc ? 'Cadastre o CPF ou CNPJ deste cliente antes de selecionar' : undefined}
                        >
                          {salvandoDespachanteCliente ? 'Adicionando...' : 'Selecionar'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {modalCadastroClienteAberto && (
        <CadastrarClienteModal
          onSalvo={handleClienteCadastradoModal}
          onClose={() => { setModalCadastroClienteAberto(false); setContextoCadastroCliente(null); }}
          API_BASE_URL={API_BASE_URL}
          addToast={addToast}
        />
      )}

      {modalEdicaoClienteAberto && clienteEmEdicao && (
        <EditarClienteModal
          cliente={clienteEmEdicao}
          onSave={handleClienteEditadoModal}
          onClose={() => { setModalEdicaoClienteAberto(false); setClienteEmEdicao(null); }}
          API_BASE_URL={API_BASE_URL}
          addToast={addToast}
        />
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .print-area { box-shadow: none !important; padding: 0 !important; }
          body { background: #fff !important; }
        }
        .print-only { display: none; }
      `}</style>
    </div>
  );
}

// ─── Estilos ───────────────────────────────────────────────────────────────────
const p = {
  page: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f0f4f8', fontFamily: "'Segoe UI', sans-serif" },
  wrap: { maxWidth: 1300, margin: '0 auto', width: '100%', padding: '20px 24px', boxSizing: 'border-box' },

  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#1a2e35' },
  subtitle: { margin: '4px 0 0', fontSize: 12, color: '#7f8c8d' },
  headerActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  dataBox: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 8, padding: '6px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  dataInput: { border: 'none', outline: 'none', fontSize: 13, fontWeight: 600, color: '#2c3e50' },
  btnHoje: { padding: '4px 10px', background: '#f0f4f8', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#008080' },
  btnRecarregar: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#555', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  btnPeriodoAtivo: { background: 'linear-gradient(135deg, #008080, #20b2aa)', color: '#fff', border: 'none' },
  btnConfig: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', color: '#555', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },

  btnImprimir: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px',
    background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, color: '#555', boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    maxWidth: 240, flexShrink: 1, minWidth: 0,
  },
  btnImprimirTexto: {
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },

  despachantesInput: { padding: '6px 10px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 12, width: 150, outline: 'none' },
  btnAddPequeno: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, padding: '0 8px', background: 'linear-gradient(135deg, #008080, #20b2aa)', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer' },

  tabsBar: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  tabBtn: { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#555' },
  tabBtnAtivo: { background: 'linear-gradient(135deg, #008080, #20b2aa)', color: '#fff', border: 'none' },

  content: { background: '#fff', borderRadius: 10, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  printHeader: { marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid #008080' },
  printTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: '#004d4d' },
  printSubtitle: { margin: '4px 0 0', fontSize: 12, color: '#555' },

  board: { display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 6 },
  coluna: { minWidth: 170, background: '#f8fffe', border: '1px solid #e0f5f2', borderRadius: 8, display: 'flex', flexDirection: 'column', flexShrink: 0 },

  gridWrap: { overflowX: 'auto', border: '1px solid #ccc', borderRadius: 6 },
  gridPresencaEtiqueta: { position: 'absolute', top: 0, right: 0, maxWidth: '95%', padding: '0 4px', fontSize: 9, lineHeight: '13px', fontWeight: 600, color: '#fff', borderBottomLeftRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', pointerEvents: 'none' },
  gridResumo: { display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 18, marginTop: 6, padding: '6px 12px', background: '#f1f8f7', border: '1px solid #cfe3e0', borderRadius: 6, fontSize: 13, color: '#2c3e50' },
  gridTable: { borderCollapse: 'collapse', width: '100%', minWidth: 900, userSelect: 'none' },
  gridTh: { border: '1px solid #ccc', padding: 0, background: 'linear-gradient(135deg, #004d4d, #008080)', minWidth: 90 },
  gridThInput: { width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', background: 'transparent', color: '#fff', fontWeight: 700, fontSize: 12, textAlign: 'center', textTransform: 'uppercase', padding: '8px 6px' },
  gridTd: { border: '1px solid #ccc', padding: 0 },
  gridTdInput: { width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', background: 'transparent', color: '#2c3e50', fontWeight: 600, fontSize: 12, textAlign: 'center', textTransform: 'uppercase', padding: '7px 6px' },
  gridTdSelecionada: { background: 'rgba(0,128,128,0.14)' },
  gridTdAncora: { boxShadow: 'inset 0 0 0 2px #008080' },
  colunaHeader: { background: 'linear-gradient(135deg, #004d4d, #008080)', color: '#fff', padding: '8px 12px', fontSize: 12, fontWeight: 700, borderRadius: '8px 8px 0 0', textAlign: 'center' },
  colunaBody: { padding: 8, display: 'flex', flexDirection: 'column', gap: 5 },
  placaRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: '5px 8px', fontSize: 12, fontWeight: 600, color: '#2c3e50' },
  placaData: { fontSize: 10, fontWeight: 500, color: '#999' },

  formRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'flex-start' },
  select: { padding: '8px 10px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 13, minWidth: 160 },
  selectInline: { padding: '4px 6px', border: '1px solid #ddd', borderRadius: 6, fontSize: 11 },
  inputSmall: { padding: '8px 10px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 13, minWidth: 140 },
  btnAdd: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'linear-gradient(135deg, #008080, #20b2aa)', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  btnDelete: { background: 'none', border: 'none', cursor: 'pointer', color: '#e74c3c', padding: 4, display: 'flex', alignItems: 'center' },
  checkbox: { width: 17, height: 17, cursor: 'pointer', accentColor: '#008080' },
  inputInline: { padding: '5px 8px', border: '1.5px solid #008080', borderRadius: 6, fontSize: 12, width: '100%', boxSizing: 'border-box' },
  acoesInline: { display: 'flex', gap: 4 },
  btnEditInline: { background: 'none', border: 'none', cursor: 'pointer', color: '#3498db', padding: 4, display: 'flex', alignItems: 'center' },
  btnSalvarInline: { background: 'none', border: 'none', cursor: 'pointer', color: '#27ae60', padding: 4, display: 'flex', alignItems: 'center' },
  btnCancelarInline: { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 4, display: 'flex', alignItems: 'center' },

  btnDesabilitado: { opacity: 0.35, cursor: 'not-allowed' },

  nomeClienteDestaque: { fontWeight: 700, fontSize: 13.5, color: '#1a2e35' },

  btnSelecionarCliente: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px',
    background: '#fff', border: '1.5px dashed #b2d8d8', borderRadius: 7,
    cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#008080', minWidth: 200,
  },

  btnSelecionarClienteDespachante: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 12px', marginTop: 4,
    background: '#fff', border: '1.5px dashed #b2d8d8', borderRadius: 7,
    cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#008080', width: '100%', justifyContent: 'center',
  },
  hintConfigDespachantes: { fontSize: 11.5, color: '#888', margin: '4px 0 10px 0', lineHeight: 1.4 },

  clienteSearchBox: { position: 'relative', minWidth: 200 },
  clienteSearchResults: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 7, boxShadow: '0 4px 14px rgba(0,0,0,0.12)', zIndex: 20, maxHeight: 180, overflowY: 'auto' },
  clienteSearchItem: { padding: '8px 12px', fontSize: 12.5, color: '#2c3e50', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' },
  clienteSearchHint: { padding: '14px 16px', fontSize: 13, color: '#aaa' },
  clienteChipSelecionado: { display: 'flex', alignItems: 'center', gap: 7, background: '#e6f7f5', border: '1px solid #b2e0da', borderRadius: 7, padding: '8px 12px', fontSize: 12.5, fontWeight: 600, color: '#004d4d' },
  clienteChipRemove: { background: 'none', border: 'none', cursor: 'pointer', color: '#008080', display: 'flex', alignItems: 'center', marginLeft: 4 },

  modalCliente: {
    background: '#fff', borderRadius: 12, width: '92%', maxWidth: 520, maxHeight: '80vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', overflow: 'hidden',
  },
  modalClienteBody: {
    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden', flex: 1,
  },
  inputBuscaModalCliente: {
    padding: '12px 14px', border: '1.5px solid #e0e0e0', borderRadius: 8, fontSize: 15, outline: 'none', boxSizing: 'border-box',
  },
  clienteModalLista: {
    display: 'flex', flexDirection: 'column', overflowY: 'auto', maxHeight: '55vh',
    border: '1px solid #f0f0f0', borderRadius: 8,
  },
  clienteModalItem: {
    padding: '13px 16px', fontSize: 14, color: '#2c3e50', cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
  },

  clienteModalItemLinha: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
    padding: '11px 16px', borderBottom: '1px solid #f5f5f5',
  },
  clienteModalItemInfo: {
    display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1,
  },
  clienteModalItemNome: {
    fontSize: 14, fontWeight: 600, color: '#2c3e50', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  docBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
    fontSize: 10.5, fontWeight: 600, color: '#008080', background: '#e6f7f5',
    border: '1px solid #b8e0dc', borderRadius: 6, padding: '2px 7px', width: 'fit-content',
  },
  docBadgeAlerta: {
    display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
    fontSize: 10, fontWeight: 600, color: '#e07030', background: '#fff5eb',
    border: '1px solid #ffd8b0', borderRadius: 6, padding: '2px 7px', width: 'fit-content',
  },
  clienteModalAcoes: { display: 'flex', gap: 6, flexShrink: 0 },
  btnEditarClienteModal: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '7px 10px',
    background: 'linear-gradient(135deg, #e07030, #f0a060)', color: '#fff', border: 'none',
    borderRadius: 7, cursor: 'pointer', fontSize: 12,
  },
  btnSelecionarClienteModal: {
    padding: '7px 12px', background: 'linear-gradient(135deg, #008080, #20b2aa)', color: '#fff',
    border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap',
  },
  btnSelecionarClienteModalDisabled: { background: '#bdc3c7', cursor: 'not-allowed', opacity: 0.85 },

  editClienteModal: {
    background: '#fff', borderRadius: 12, width: '92%', maxWidth: 620, maxHeight: '88vh',
    display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', overflow: 'hidden',
  },
  editClienteBody: {
    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', flex: 1,
  },
  editRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  editGroup: { flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  editLabel: { fontSize: 11, fontWeight: 700, color: '#004d4d', textTransform: 'uppercase', letterSpacing: 0.4 },
  editInput: {
    padding: '9px 12px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 13, outline: 'none',
    width: '100%', boxSizing: 'border-box',
  },
  editHint: { fontSize: 10, color: '#888' },
  cnpjRow: { display: 'flex', gap: 6, alignItems: 'stretch' },
  cnpjButton: { backgroundColor: '#3498db', color: '#fff', border: 'none', padding: '0 12px', borderRadius: 7, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  cnpjButtonDisabled: { backgroundColor: '#b8c2cc', cursor: 'not-allowed' },
  editError: { fontSize: 11, color: '#e74c3c', fontWeight: 600 },
  editSectionLabel: {
    fontSize: 11, fontWeight: 700, color: '#008080', textTransform: 'uppercase', letterSpacing: 0.5,
    marginTop: 4, paddingBottom: 6, borderBottom: '1.5px dashed #b8e0dc',
  },

  semResultadoBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    padding: '18px 16px', textAlign: 'center',
  },
  btnCadastrarNovoModal: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 16px',
    background: 'linear-gradient(135deg, #e07030, #f0a060)', color: '#fff', border: 'none',
    borderRadius: 7, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
  },

  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e0e0e0', color: '#008080', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0', color: '#2c3e50' },
  tdTotal: { padding: '8px 10px', borderTop: '2px solid #e0e0e0', fontWeight: 700, color: '#004d4d' },
  emptyRow: { textAlign: 'center', color: '#aaa', padding: 20, fontSize: 12 },

  toggleBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 14, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#888' },
  toggleBtnAtivo: { background: '#e8f8f0', borderColor: '#b2e8cc', color: '#27ae60' },

  resumoCards: { display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' },
  resumoCard: { flex: 1, minWidth: 130, display: 'flex', flexDirection: 'column', gap: 3, background: '#f8fbfb', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 14px' },
  resumoCardLabel: { fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 },
  resumoCardQtd: { fontSize: 18, fontWeight: 700, color: '#004d4d' },
  resumoCardValor: { fontSize: 12, fontWeight: 600, color: '#27ae60' },

  totaisPix: { display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' },
  totalCard: { flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 4, background: '#f8fbfb', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 14px' },
  totalCardQtd: { fontSize: 11, color: '#888', fontWeight: 600 },
  btnRealizarSaque: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8, padding: '7px 12px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, alignSelf: 'flex-start' },
  btnRealizarSaqueDisabled: { background: '#bdc3c7', cursor: 'not-allowed' },
  textoModalSaque: { fontSize: 13.5, color: '#2c3e50', margin: 0, lineHeight: 1.5 },
  hintModalSaque: { fontSize: 11.5, color: '#888', marginTop: 10 },
  hintTexto: { fontSize: 11, color: '#aaa', fontStyle: 'italic', marginTop: 8 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modalConfig: { background: '#fff', borderRadius: 12, width: '92%', maxWidth: 440, maxHeight: '86vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', overflow: 'hidden' },
  modalConfigHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: 'linear-gradient(135deg, #004d4d, #008080)' },
  modalConfigTitle: { display: 'flex', alignItems: 'center', gap: 8, color: '#fff', fontWeight: 700, fontSize: 14 },
  modalConfigClose: { background: 'none', border: 'none', color: '#fff', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  modalConfigBody: { padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', flex: 1 },
  labelConfig: { fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4 },
  toggleRowConfig: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fbfb', border: '1px solid #e0e0e0', borderRadius: 7, padding: '10px 12px', marginTop: 6 },
  toggleRowConfigText: { fontSize: 12.5, color: '#2c3e50' },
  inputConfig: { padding: '10px 12px', border: '1.5px solid #e0e0e0', borderRadius: 7, fontSize: 15, fontWeight: 600, outline: 'none' },
  modalConfigFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  btnSecondaryConfig: { padding: '8px 16px', background: '#f0f4f8', color: '#555', border: '1px solid #ddd', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 },

  btnDesativarModalConfirmar: {
    padding: '8px 18px', background: 'linear-gradient(135deg, #b03a2e, #e74c3c)', color: '#fff',
    border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700,
  },

  despachantesListaConfig: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 160, overflowY: 'auto', marginTop: 6, paddingRight: 2 },
  despachanteLinhaConfig: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fbfb', border: '1px solid #e0e0e0', borderRadius: 7, padding: '7px 10px', fontSize: 12.5, color: '#2c3e50' },
  btnDesativarConfig: { padding: '4px 10px', background: '#fff5f5', color: '#e74c3c', border: '1px solid #f5c6cb', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  despachantesAddConfig: { display: 'flex', gap: 6, marginTop: 10 },
};

export default PainelCRVA;
